import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ButtonControl, DialogControl, IconControl, Loading } from "spark-core-dx/components";
import { useCoreContext } from "spark-core-dx/contexts";
import { useCorePermissions, useCoreRoute, useGenericManager } from "spark-core-dx/hooks";
import { ItemServiceFactory } from "../../../services/ItemServiceFactory";
import "./BatchManagement.scss";

const TABS = [
  { key: "overview", label: "Overview" },
  { key: "pools", label: "Pools" },
  { key: "active-tasks", label: "Active Tasks" },
  { key: "remote-hosts", label: "Remote Hosts" },
  { key: "api-hosts", label: "API Hosts" },
  { key: "background", label: "Background" },
  { key: "scheduled", label: "Scheduled" },
  { key: "tests", label: "Tests" },
  { key: "diagnostics", label: "Diagnostics" },
];

const REMOTE_HOST_ACTIONS = [
  { operationName: "StartRemoteBatchHost", label: "Ensure Ready" },
  { operationName: "CheckRemoteBatchHostStatus", label: "Check" },
  { operationName: "DrainRemoteBatchHost", label: "Drain" },
  { operationName: "RestartRemoteBatchHost", label: "Restart" },
  { operationName: "StopRemoteBatchHost", label: "Stop" },
  { operationName: "RunRemoteBatchHostManualTest", label: "Test", isManualTest: true },
];

const API_HOST_ACTIONS = [
  { operationName: "StartAPIEndpointBatchHost", label: "Deploy" },
  { operationName: "CheckAPIEndpointBatchHostStatus", label: "Check" },
  { operationName: "StopAPIEndpointBatchHost", label: "Drop" },
];

const POOL_ACTIONS = [
  { label: "Check", isManualTest: true },
  { label: "Ensure", operationName: "createupdatebatchpool" },
];

const PROCESSOR_TYPE_LABELS = {
  1: "RealTime",
  2: "Async",
  3: "Long Running",
  4: "Scheduled",
  5: "Stream",
  6: "Long Running VM",
  7: "Long Running App Svc",
  8: "Remote",
};

const EVENT_STATUS_LABELS = {
  0: "Failed",
  1: "Created",
  2: "In Progress",
  3: "Completed",
  4: "Cancelled",
  5: "Scheduled",
  6: "Paused",
};

const DESTINATION_STATUS_LABELS = {
  0: "Unavailable",
  1: "Online",
  2: "Offline",
};

const REMOTE_HOST_REFERENCE_TYPE_LABELS = {
  0: "Default",
  1: "Workspace",
  2: "Pool Profile",
  3: "Shard",
  4: "Workload",
};

const REMOTE_HOST_STALE_SECONDS = 60;
const AUTO_REFRESH_INTERVAL_SECONDS = 15;
const REMOTE_HOST_STARTUP_RECOVERY_TIMEOUT_MS = 15 * 60 * 1000;
const DEBUG_LOCAL_STARTUP_RECOVERY_TIMEOUT_MS = 30 * 1000;
const MAX_MANUAL_TEST_HISTORY_ITEMS = 50;
const MAX_REMOTE_HOST_STARTUP_MESSAGES = 40;
const BATCH_MANAGEMENT_PREFERENCE_KEY = "BatchManagement";
const ACTIVE_STARTUP_EVENT_STATUSES = new Set([1, 2]);
const DEBUG_LOCAL_HOST_PROJECT_COMMAND = "dotnet run --project CivicDX.Core.EventProcessorBatch --";
const DEBUG_LOCAL_API_HOST_PROJECT_COMMAND = "powershell -ExecutionPolicy Bypass -File .\\Scripts\\Test\\Start-LocalAPIEndpointBatchHost.ps1";

const unwrapResult = (response) => {
  if (Array.isArray(response)) {
    return response[0] ?? null;
  }

  return response ?? null;
};

const getNormalizedSingleResult = (response) => (Array.isArray(response) ? response[0] ?? null : response ?? null);

const getItems = (result) => {
  const items = result?.Items;
  if (!items) {
    return [];
  }

  if (Array.isArray(items)) {
    return items;
  }

  if (items.toArray instanceof Function) {
    return items.toArray();
  }

  if (typeof items[Symbol.iterator] === "function") {
    return Array.from(items);
  }

  if (items.forEach instanceof Function) {
    const collectedItems = [];
    items.forEach((item) => {
      collectedItems.push(item);
    });
    return collectedItems;
  }

  if (items.first instanceof Function) {
    return [items.first()].filter(Boolean);
  }

  return [];
};

const formatDateTime = (value) => {
  const raw = String(value ?? "").trim();
  if (!raw) {
    return "-";
  }

  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) {
    return raw;
  }

  return parsed.toLocaleString();
};

const formatScheduledEndDate = (value) => {
  const parsed = getEntryDateValue(value);
  if (parsed && parsed.getUTCFullYear() > 2100) {
    return "-";
  }

  return formatDateTime(value);
};

const formatDurationCompact = (value) => {
  const raw = String(value ?? "").trim();
  if (!raw) {
    return "";
  }

  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) {
    return "";
  }

  const diffMs = Date.now() - parsed.getTime();
  if (diffMs <= 0) {
    return "just now";
  }

  const totalMinutes = Math.floor(diffMs / 60000);
  const totalHours = Math.floor(totalMinutes / 60);
  const totalDays = Math.floor(totalHours / 24);

  if (totalDays > 0) {
    const hours = totalHours % 24;
    return hours > 0 ? `${totalDays}d ${hours}h` : `${totalDays}d`;
  }

  if (totalHours > 0) {
    const minutes = totalMinutes % 60;
    return minutes > 0 ? `${totalHours}h ${minutes}m` : `${totalHours}h`;
  }

  if (totalMinutes > 0) {
    return `${totalMinutes}m`;
  }

  return "under 1m";
};

const getEntryDateValue = (value) => {
  const raw = String(value ?? "").trim();
  if (!raw) {
    return null;
  }

  const parsed = new Date(raw);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const getDestinationStatusLabel = (value) => {
  if (value === undefined || value === null || value === "") {
    return "Unknown";
  }

  const numericValue = Number(value);
  return DESTINATION_STATUS_LABELS[numericValue] ?? String(value);
};

const isRemoteHostStale = (host) => {
  if (Number(host?.Status ?? 0) !== 2) {
    return false;
  }

  const heartbeatDate = getEntryDateValue(host?.LastHeartbeatDateTime);
  if (!heartbeatDate) {
    return false;
  }

  return Date.now() - heartbeatDate.getTime() > REMOTE_HOST_STALE_SECONDS * 1000;
};

const isRemoteHostStartupPending = (host) => Number(host?.Status ?? 0) === 1;
const isAPIHostStale = (host) => isRemoteHostStale(host);
const isAPIEndpointHostTask = (entry) =>
  String(entry?.ScopeType ?? "").trim().toLowerCase() === "api endpoint host";

const getRemoteHostStartupAnchorDate = (host) =>
  getEntryDateValue(
    host?.LastStartupRequestedDateTime
    ?? host?.LastStartedDateTime
    ?? host?.UpdatedDate
  );

const isRemoteHostStartupRecoverable = (host) => {
  if (!isRemoteHostStartupPending(host)) {
    return false;
  }

  const anchorDate = getRemoteHostStartupAnchorDate(host);
  if (!anchorDate) {
    return false;
  }

  const recoveryTimeoutMs = host?.IsDebugLocal
    ? DEBUG_LOCAL_STARTUP_RECOVERY_TIMEOUT_MS
    : REMOTE_HOST_STARTUP_RECOVERY_TIMEOUT_MS;

  return Date.now() - anchorDate.getTime() <= recoveryTimeoutMs;
};

const hasRemoteHostStartupEvent = (host) => String(host?.StartupEventId ?? "").trim() !== "";

const isRemoteHostStartupResumable = (host) =>
  isRemoteHostStartupRecoverable(host) && hasRemoteHostStartupEvent(host);

const isStartupEventStatusActive = (eventItem) =>
  ACTIVE_STARTUP_EVENT_STATUSES.has(Number(eventItem?.EventStatus ?? eventItem?.Status ?? -1));

const getRemoteHostStateLabel = (host) => {
  if (!host) {
    return "Unknown";
  }

  if (isRemoteHostStale(host)) {
    return "Stale";
  }

  switch (Number(host?.Status ?? 0)) {
    case 1:
      return "Starting";
    case 2:
      return "Online";
    case 3:
      return "Busy";
    case 4:
      return "Offline";
    case 5:
      return "Error";
    case 6:
      return "Stopping";
    case 7:
      return "Draining";
    default:
      return String(host?.Status ?? "Unknown");
  }
};

const escapePowerShellArgument = (value) => `"${String(value ?? "").replaceAll("\"", "\\\"")}"`;

const getDebugLocalHostCommand = (host) => {
  if (!host?._id) {
    return "";
  }

  const parts = [
    DEBUG_LOCAL_HOST_PROJECT_COMMAND,
    "-host",
    "--enabled", "true",
    "--debug-local", "true",
    "--host-registration-id", escapePowerShellArgument(host._id),
    "--assigned-client-short-name", escapePowerShellArgument(host?.ClientShortName ?? "DEFAULT"),
    "--assigned-reference-type", escapePowerShellArgument(
      Number(host?.ReferenceType ?? 0) === 0
        ? "None"
        : (REMOTE_HOST_REFERENCE_TYPE_LABELS[Number(host?.ReferenceType ?? 0)] ?? "None")
    ),
  ];

  if (host?.ClientId) {
    parts.push("--assigned-client-id", escapePowerShellArgument(host.ClientId));
  }

  if (host?.ReferenceId) {
    parts.push("--assigned-reference-id", escapePowerShellArgument(host.ReferenceId));
  }

  if (host?.Endpoint) {
    parts.push("--advertised-endpoint", escapePowerShellArgument(host.Endpoint));
  }

  return parts.join(" ");
};

const getDebugLocalAPIHostCommand = (host) => {
  const deploymentId = String(host?.SchemaAPIDeploymentId ?? "").trim();
  const apiName = String(host?.APIName ?? "").trim();
  const deploymentKey = String(host?.DeploymentKey ?? "").trim();

  if (!deploymentId && (!apiName || !deploymentKey)) {
    return "";
  }

  const parts = [DEBUG_LOCAL_API_HOST_PROJECT_COMMAND];

  if (deploymentId) {
    parts.push("-DeploymentId", escapePowerShellArgument(deploymentId));
  } else {
    parts.push("-ApiName", escapePowerShellArgument(apiName));
    parts.push("-DeploymentKey", escapePowerShellArgument(deploymentKey));
  }

  const listenPort = Number(host?.BatchListenPort ?? 0);
  if (Number.isFinite(listenPort) && listenPort > 0) {
    parts.push("-ListenPort", String(listenPort));
  }

  if (host?.PublicBaseUrl) {
    parts.push("-PublicBaseUrl", escapePowerShellArgument(host.PublicBaseUrl));
  }

  return parts.join(" ");
};

const getRemoteHostDisplayStateLabel = (host) => {
  const baseLabel = getRemoteHostStateLabel(host);

  if (host?.IsDebugLocal) {
    switch (baseLabel) {
      case "Starting":
        return "Local Starting";
      case "Online":
        return "Local Online";
      case "Offline":
        return "Local Offline";
      case "Error":
        return "Local Error";
      case "Busy":
        return "Local Busy";
      case "Stopping":
        return "Local Stopping";
      case "Draining":
        return "Local Draining";
      case "Stale":
        return "Local Stale";
      default:
        return `Local ${baseLabel}`;
    }
  }

  return baseLabel;
};

const getRemoteHostRouteLabel = (host) => {
  const referenceTypeNumber = Number(host?.ReferenceType ?? 0);
  const referenceTypeLabel = REMOTE_HOST_REFERENCE_TYPE_LABELS[referenceTypeNumber] ?? "Default";
  const referenceId = String(host?.ReferenceId ?? "").trim();

  if (!referenceId || referenceTypeNumber === 0) {
    return "Client | Default Route";
  }

  return `Client | ${referenceTypeLabel}: ${referenceId}`;
};

const getRemoteHostUptimeLabel = (host) => {
  const uptime = formatDurationCompact(host?.LastStartedDateTime);
  return uptime ? `Up ${uptime}` : "";
};

const getAPIHostStateLabel = (host) => getRemoteHostDisplayStateLabel(host);

const getAPIHostUptimeLabel = (host) => {
  const uptime = formatDurationCompact(host?.LastStartedDateTime);
  return uptime ? `Up ${uptime}` : "";
};

const getAPIHostLifecycleDetail = (host) => {
  if (host?.LastStatusDetail) {
    return host.LastStatusDetail;
  }

  if (host?.LastErrorMessage) {
    return host.LastErrorMessage;
  }

  if (host?.Endpoint) {
    return host?.IsDebugLocal ? "Local host registration is active." : "Host registration is active.";
  }

  return "No recent lifecycle detail.";
};

const buildAPIHostOpenApiUrl = (host) => {
  const baseUrl = String(host?.PublicBaseUrl ?? "").trim().replace(/\/+$/, "");
  const apiName = String(host?.APIName ?? "").trim();
  const deploymentKey = String(host?.DeploymentKey ?? "").trim();

  if (!baseUrl || !apiName || !deploymentKey) {
    return "";
  }

  return `${baseUrl}/api/endpoint/v1/${encodeURIComponent(apiName)}/${encodeURIComponent(deploymentKey)}/openapi.json`;
};

const getRecoveredRemoteHostStartupEntry = (host) => {
  if (!host || !isRemoteHostStartupResumable(host)) {
    return null;
  }

  const message = String(
    host?.LastStatusDetail
    ?? host?.LastErrorMessage
    ?? "Startup requested. Waiting for host registration."
  ).trim();

  if (!message) {
    return null;
  }

  return {
    id: `${host?._id ?? "host"}:recovered-startup`,
    eventId: "",
    message,
    stage: "waiting",
    uiMessageType: "status",
    timestamp: host?.LastStartupRequestedDateTime ?? host?.UpdatedDate ?? new Date().toISOString(),
    outputType: "text",
  };
};

const isRemoteHostPayload = (payload) => {
  if (!payload || typeof payload !== "object") {
    return false;
  }

  return [
    "ReferenceType",
    "HostInstanceId",
    "CurrentJobId",
    "CurrentTaskId",
    "Endpoint",
    "LastHeartbeatDateTime",
    "LastStatusDetail",
  ].some((key) => Object.prototype.hasOwnProperty.call(payload, key));
};

const getPayloadStatusLabel = (payload) => {
  if (!payload) {
    return "";
  }

  const rawStatus = payload?.Status;
  if (rawStatus === undefined || rawStatus === null || rawStatus === "") {
    return "";
  }

  if (isRemoteHostPayload(payload)) {
    return getRemoteHostStateLabel(payload);
  }

  return String(rawStatus);
};

const getStatusToneClass = (value) => {
  const normalized = String(value ?? "").trim().toLowerCase();
  if (
    normalized.includes("error") ||
    normalized.includes("fail") ||
    normalized.includes("offline")
  ) {
    return "is-error";
  }

  if (
    normalized.includes("start") ||
    normalized.includes("progress") ||
    normalized.includes("scheduled") ||
    normalized.includes("stale") ||
    normalized.includes("busy") ||
    normalized.includes("stop") ||
    normalized.includes("drain")
  ) {
    return "is-warn";
  }

  if (
    normalized.includes("online") ||
    normalized.includes("healthy") ||
    normalized.includes("passed")
  ) {
    return "is-ok";
  }

  return "";
};

const getActiveTaskActionKey = (entry, actionKey) =>
  `${actionKey}:${entry?.HostId ?? "host"}:${entry?.EventId ?? "event"}:${entry?.JobId ?? "job"}:${entry?.TaskId ?? "task"}`;

const getScheduledActionKey = (entry, actionKey) =>
  `${actionKey}:${entry?.EventId ?? entry?._id ?? "scheduled"}`;

const getAPIHostRowKey = (host) => {
  const hostId = String(host?._id ?? "").trim();
  if (hostId) {
    return hostId;
  }

  const deploymentId = String(host?.SchemaAPIDeploymentId ?? "").trim();
  if (deploymentId) {
    return `deployment:${deploymentId}`;
  }

  return `api-host:${host?.APIName ?? "api"}:${host?.DeploymentKey ?? "deployment"}`;
};

const getGuidOperationId = (value) => {
  const raw = String(value ?? "").trim();
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(raw) ? raw : "";
};

const renderRowActionButtonContent = (label, isLoading = false) =>
  isLoading ? (
    <span className="row-action-content is-loading">
      <span className="row-action-spinner" />
      <span>{label}</span>
    </span>
  ) : (
    label
  );

const getDisplayMessage = (result, fallbackMessage) =>
  result?.DisplayMessage ??
  result?.Message?.DisplayMessage ??
  fallbackMessage;

const getActionResultPayload = (result) => getItems(result)[0] ?? null;

const getActionResultDetailLines = (result) => {
  const payload = getActionResultPayload(result);
  const detailEntries = [
    ...(result?.MessageDetails ?? []),
    ...String(payload?.Details ?? "")
      .split(/\r?\n/g)
      .map((entry) => entry.trim()),
  ];

  return detailEntries.filter(
    (entry, index, array) =>
      !!entry &&
      entry !== result?.DisplayMessage &&
      entry !== payload?.Message &&
      array.indexOf(entry) === index
  );
};

const buildRemoteHostStartupFailureResult = ({
  hostId = "",
  fallbackMessage = "Remote host startup failed.",
  result = null,
  payload = null,
  host = null,
  eventId = "",
}) => {
  const displayMessage =
    payload?.Message
    ?? host?.LastErrorMessage
    ?? host?.LastStatusDetail
    ?? result?.DisplayMessage
    ?? fallbackMessage;

  const detailEntries = [
    ...(result?.MessageDetails ?? []),
    payload?.Message,
    host?.LastErrorMessage,
    host?.LastStatusDetail,
  ]
    .map((entry) => String(entry ?? "").trim())
    .filter((entry, index, array) => !!entry && array.indexOf(entry) === index);

  const failureResult = {
    Success: false,
    DisplayMessage: displayMessage,
    MessageDetails: detailEntries.length > 0 ? detailEntries : [displayMessage],
    EventId: String(
      eventId
      ?? payload?.EventId
      ?? result?.EventId
      ?? ""
    ).trim(),
  };

  console.error("Remote host startup failed.", {
    hostId,
    failureResult,
    response: result,
    payload,
    host,
  });

  return failureResult;
};

const createManualTestHistoryEntryId = () => {
  if (typeof window !== "undefined" && window.crypto?.randomUUID instanceof Function) {
    return window.crypto.randomUUID();
  }

  return `manual-test:${Date.now()}:${Math.random().toString(36).slice(2, 8)}`;
};

const parseJsonObject = (value) => {
  if (!value) {
    return {};
  }

  if (typeof value === "string") {
    try {
      const parsedValue = JSON.parse(value);
      return parsedValue && typeof parsedValue === "object" && !Array.isArray(parsedValue)
        ? parsedValue
        : {};
    } catch {
      return {};
    }
  }

  return value && typeof value === "object" && !Array.isArray(value)
    ? value
    : {};
};

const serializeJsonValue = (value) => {
  try {
    return JSON.stringify(value ?? {}, null, 2);
  } catch {
    return "{}";
  }
};

const getManualTestHistoryMetadata = (entry) => {
  return parseJsonObject(entry?.Metadata ?? entry?.metadata ?? {});
};

const normalizeManualTestHistoryEntry = (entry, index = 0) => {
  if (!entry || typeof entry !== "object") {
    return null;
  }

  const metadata = getManualTestHistoryMetadata(entry);
  const result = metadata.Result ?? metadata.result ?? entry.Result ?? entry.result ?? null;
  const payload = metadata.Payload ?? metadata.payload ?? entry.Payload ?? entry.payload ?? getActionResultPayload(result);
  const checkedDateTimeValue =
    entry.CheckedDateTime
    ?? entry.checkedDateTime
    ?? payload?.CheckedDateTime
    ?? metadata.CheckedDateTime
    ?? new Date().toISOString();
  const checkedDateTime =
    getEntryDateValue(checkedDateTimeValue)?.toISOString()
    ?? String(checkedDateTimeValue);
  const rawId =
    entry._id
    ?? entry.UniqueId
    ?? entry.id
    ?? `${entry.HistoryKey ?? entry.key ?? "manual-test"}:${checkedDateTime}:${index}`;

  return {
    id: String(rawId),
    key: String(entry.HistoryKey ?? entry.Key ?? entry.key ?? ""),
    label: String(entry.Label ?? entry.label ?? "Manual Test"),
    scopeLabel: String(entry.ScopeLabel ?? entry.scopeLabel ?? ""),
    targetId: String(entry.TargetId ?? entry.targetId ?? ""),
    operationName: String(entry.OperationName ?? entry.operationName ?? ""),
    result,
    payload,
    checkedDateTime,
    status: String(
      entry.Status
      ?? entry.status
      ?? payload?.Status
      ?? (result?.Success ? "Passed" : "Failed")
      ?? ""
    ),
    displayMessage: String(
      entry.DisplayMessage
      ?? entry.displayMessage
      ?? result?.DisplayMessage
      ?? ""
    ),
  };
};

const normalizeManualTestHistory = (entries) => {
  if (!Array.isArray(entries)) {
    return [];
  }

  return entries
    .map((entry, index) => normalizeManualTestHistoryEntry(entry, index))
    .filter(Boolean)
    .sort((left, right) => {
      const leftDate = getEntryDateValue(left?.checkedDateTime)?.getTime() ?? 0;
      const rightDate = getEntryDateValue(right?.checkedDateTime)?.getTime() ?? 0;
      return rightDate - leftDate;
    })
    .slice(0, MAX_MANUAL_TEST_HISTORY_ITEMS);
};

const parseManualTestHistoryJson = (value) => {
  try {
    const parsedValue = typeof value === "string" ? JSON.parse(value) : value;
    return normalizeManualTestHistory(parsedValue);
  } catch {
    return [];
  }
};

const toManualTestHistoryPreferenceItems = (entries) =>
  normalizeManualTestHistory(entries).map((entry) => ({
    UniqueId: entry.id || createManualTestHistoryEntryId(),
    HistoryKey: entry.key ?? "",
    Label: entry.label ?? "Manual Test",
    ScopeLabel: entry.scopeLabel ?? "",
    TargetId: entry.targetId ?? "",
    OperationName: entry.operationName ?? "",
    CheckedDateTime: entry.checkedDateTime ?? new Date().toISOString(),
    Status: entry.status ?? entry.payload?.Status ?? (entry.result?.Success ? "Passed" : "Failed"),
    DisplayMessage: entry.displayMessage ?? entry.result?.DisplayMessage ?? "",
    Metadata: serializeJsonValue({
      Result: entry.result ?? null,
      Payload: entry.payload ?? getActionResultPayload(entry.result),
    }),
  }));

const toDiagnosticJson = (value) => {
  try {
    return JSON.stringify(value ?? {}, null, 2);
  } catch {
    return String(value ?? "");
  }
};

const getRemoteHostLifecycleDetail = (host) => {
  if (host?.LastStatusDetail) {
    return host.LastStatusDetail;
  }

  if (host?.LastErrorMessage) {
    return host.LastErrorMessage;
  }

  if (host?.IsDebugLocal) {
    if (Number(host?.Status ?? 0) === 1) {
      return "Waiting for debug-local host registration.";
    }

    if (Number(host?.Status ?? 0) === 5 || Number(host?.Status ?? 0) === 4) {
      return "Debug-local host is not running.";
    }
  }

  return host?.Endpoint ? "Host registration is active." : "No recent lifecycle detail.";
};

const createManualTestHistoryEntry = (manualTestOption, result) => {
  const payload = getActionResultPayload(result);
  const checkedDateTime = payload?.CheckedDateTime ?? new Date().toISOString();

  return {
    id: createManualTestHistoryEntryId(),
    key: manualTestOption?.key ?? "",
    label: manualTestOption?.label ?? "Manual Test",
    scopeLabel: manualTestOption?.scopeLabel ?? "",
    targetId: manualTestOption?.targetId ?? "",
    operationName: manualTestOption?.operationName ?? "",
    result,
    payload,
    checkedDateTime,
  };
};

const getManualTestOptions = (remoteHosts) => [
  {
    key: "azure-batch",
    operationName: "RunAzureBatchManualTest",
    label: "Azure Batch Manual Test",
    description: "Checks Azure Batch connectivity, configured pools, and the default pool state.",
    scopeLabel: "System",
    targetId: "",
    isHostTest: false,
  },
  ...remoteHosts.map((host) => ({
    key: `host:${host?._id}`,
    operationName: "RunRemoteBatchHostManualTest",
    label: `${host?.Name ?? "Remote Batch Host"} | Test`,
    description: "Runs one end-to-end remote test through the normal dispatch path and executes server-side capability checks on the host.",
    scopeLabel: host?.ClientShortName ?? "DEFAULT",
    targetId: host?._id ?? "",
    isHostTest: true,
    status: getRemoteHostStateLabel(host),
    endpoint: host?.Endpoint ?? "",
  })),
].filter((entry) => !!entry.targetId || !entry.isHostTest);

const getPoolManualTestOptions = (poolProfiles) =>
  poolProfiles.map((poolProfile) => ({
    key: `pool:${poolProfile?._id}`,
    operationName: "CheckAzureBatchPoolStatus",
    label: `${poolProfile?.Name ?? "Pool Profile"}${poolProfile?.Version ? ` v${poolProfile.Version}` : ""}`,
    description: `Checks the Azure Batch runtime state for pool ${poolProfile?.PoolId ?? "(unknown)"}.`,
    scopeLabel: poolProfile?.IsDefault ? "Default Pool" : "Pool Profile",
    targetId: poolProfile?._id ?? "",
    isPoolTest: true,
    poolId: poolProfile?.PoolId ?? "",
  })).filter((entry) => !!entry.targetId);

const sortPoolProfiles = (poolProfiles) =>
  [...poolProfiles].sort((left, right) => {
    const leftActive = left?.IsActive === false ? 0 : 1;
    const rightActive = right?.IsActive === false ? 0 : 1;
    if (leftActive !== rightActive) {
      return rightActive - leftActive;
    }

    const leftDefault = left?.IsDefault === true ? 1 : 0;
    const rightDefault = right?.IsDefault === true ? 1 : 0;
    if (leftDefault !== rightDefault) {
      return rightDefault - leftDefault;
    }

    return String(left?.Name ?? "").localeCompare(String(right?.Name ?? ""), undefined, {
      sensitivity: "base",
    });
  });

const renderCapabilityResults = (capabilityResults) => {
  if (!Array.isArray(capabilityResults) || capabilityResults.length === 0) {
    return null;
  }

  return (
    <div className="mini-list batch-management-capability-results">
      {capabilityResults.map((entry, index) => (
        <div
          key={`${entry?.Name ?? "capability"}:${entry?.CheckedDateTime ?? index}`}
          className="mini-list-row"
        >
          <div>
            <div className="mini-list-title">{entry?.Name ?? "Capability"}</div>
            <div className="mini-list-detail">
              {entry?.Message ?? "-"}
              {entry?.DurationMs ? ` | ${entry.DurationMs} ms` : ""}
            </div>
            {entry?.Details ? (
              <div className="mini-list-detail">{entry.Details}</div>
            ) : null}
          </div>
          <div className={`mini-list-status ${getStatusToneClass(entry?.Status)}`}>
            {entry?.Status ?? "-"}
          </div>
        </div>
      ))}
    </div>
  );
};

const BatchManagement = () => {
  const { canView } = useCorePermissions();
  const coreContext = useCoreContext();
  const itemServiceFactory = useMemo(() => new ItemServiceFactory(), []);
  const genericMgr = useGenericManager("Generic");
  const remoteBatchHostMgr = useGenericManager(itemServiceFactory.ItemServiceEnum.RemoteBatchHost);
  const apiEndpointBatchHostMgr = useGenericManager("APIEndpointBatchHost");
  const batchManagementPreferenceMgr = useGenericManager(itemServiceFactory.ItemServiceEnum.BatchManagementPreference);
  const poolProfileMgr = useGenericManager("PoolProfile");
  const eventMgr = useGenericManager("Event");
  const navigate = useNavigate();
  const { getRoute } = useCoreRoute();
  const genericMgrRef = useRef(genericMgr);
  const remoteBatchHostMgrRef = useRef(remoteBatchHostMgr);
  const apiEndpointBatchHostMgrRef = useRef(apiEndpointBatchHostMgr);
  const batchManagementPreferenceMgrRef = useRef(batchManagementPreferenceMgr);
  const poolProfileMgrRef = useRef(poolProfileMgr);
  const eventMgrRef = useRef(eventMgr);
  const refreshInFlightRef = useRef(false);
  const remoteHostStreamControllersRef = useRef(new Map());

  const [activeTab, setActiveTab] = useState("overview");
  const [summary, setSummary] = useState(null);
  const [activeTasks, setActiveTasks] = useState([]);
  const [remoteHosts, setRemoteHosts] = useState([]);
  const [apiHosts, setAPIHosts] = useState([]);
  const [selectedRemoteHostId, setSelectedRemoteHostId] = useState("");
  const [selectedAPIHostId, setSelectedAPIHostId] = useState("");
  const [remoteHostStartupFeeds, setRemoteHostStartupFeeds] = useState({});
  const [remoteHostStartupEventStates, setRemoteHostStartupEventStates] = useState({});
  const [backgroundProcesses, setBackgroundProcesses] = useState([]);
  const [scheduledWork, setScheduledWork] = useState([]);
  const [poolProfiles, setPoolProfiles] = useState([]);
  const [loadingState, setLoadingState] = useState({
    summary: false,
    activeTasks: false,
    remoteHosts: false,
    apiHosts: false,
    background: false,
    scheduled: false,
    pools: false,
  });
  const [sectionErrors, setSectionErrors] = useState({
    summary: "",
    activeTasks: "",
    remoteHosts: "",
    apiHosts: "",
    background: "",
    scheduled: "",
    pools: "",
  });
  const [headerActionKey, setHeaderActionKey] = useState("");
  const [hostActionKey, setHostActionKey] = useState("");
  const [apiHostActionKey, setAPIHostActionKey] = useState("");
  const [lastActionResult, setLastActionResult] = useState(null);
  const [isLastActionDialogOpen, setIsLastActionDialogOpen] = useState(false);
  const [poolActionKey, setPoolActionKey] = useState("");
  const [activeTaskActionKey, setActiveTaskActionKey] = useState("");
  const [scheduledActionKey, setScheduledActionKey] = useState("");
  const [isManualTestDialogOpen, setIsManualTestDialogOpen] = useState(false);
  const [selectedManualTestKey, setSelectedManualTestKey] = useState("azure-batch");
  const [manualTestLauncherKey, setManualTestLauncherKey] = useState("");
  const [activeManualTestActionKey, setActiveManualTestActionKey] = useState("");
  const [manualTestResult, setManualTestResult] = useState(null);
  const [batchManagementPreference, setBatchManagementPreference] = useState(null);
  const [hasLoadedBatchManagementPreference, setHasLoadedBatchManagementPreference] = useState(false);
  const [loadedBatchManagementPreferenceScopeKey, setLoadedBatchManagementPreferenceScopeKey] = useState("");
  const [manualTestHistory, setManualTestHistory] = useState([]);
  const [selectedManualTestHistoryId, setSelectedManualTestHistoryId] = useState("");
  const [isRunningManualTest, setIsRunningManualTest] = useState(false);
  const [isAutoRefreshEnabled, setIsAutoRefreshEnabled] = useState(true);
  const [lastRefreshDateTime, setLastRefreshDateTime] = useState("");
  const [copiedDebugLocalCommandHostId, setCopiedDebugLocalCommandHostId] = useState("");
  const [copiedAPIHostDebugLocalCommandKey, setCopiedAPIHostDebugLocalCommandKey] = useState("");

  const canViewBatchManagement = canView("BatchManagement");
  const lastActionPayload = useMemo(
    () => getActionResultPayload(lastActionResult),
    [lastActionResult]
  );
  const lastActionDetailLines = useMemo(
    () => getActionResultDetailLines(lastActionResult),
    [lastActionResult]
  );
  const manualTestOptions = useMemo(
    () => [...getManualTestOptions(remoteHosts), ...getPoolManualTestOptions(poolProfiles)],
    [poolProfiles, remoteHosts]
  );
  const defaultPoolProfile = useMemo(
    () => poolProfiles.find((poolProfile) => !!poolProfile?.IsDefault) ?? null,
    [poolProfiles]
  );
  const backgroundSnapshotEntries = useMemo(() => {
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - 7);

    return [...backgroundProcesses]
      .filter((entry) => {
        const entryDate = getEntryDateValue(entry?.RunDate ?? entry?.CreatedDate);
        return !!entryDate && entryDate >= cutoff;
      })
      .sort((left, right) => {
        const leftDate = getEntryDateValue(left?.RunDate ?? left?.CreatedDate)?.getTime() ?? 0;
        const rightDate = getEntryDateValue(right?.RunDate ?? right?.CreatedDate)?.getTime() ?? 0;
        return rightDate - leftDate;
      })
      .slice(0, 3);
  }, [backgroundProcesses]);
  const selectedManualTest = useMemo(
    () =>
      manualTestOptions.find((option) => option.key === selectedManualTestKey)
      ?? manualTestOptions[0]
      ?? null,
    [manualTestOptions, selectedManualTestKey]
  );
  const eventManageBaseRoute = useMemo(
    () => getRoute("/event/manage/", "Convoke")?.RoutePath ?? "/admin/event/manage",
    [getRoute]
  );
  const poolProfileBaseRoute = useMemo(
    () => getRoute("/dynamic/poolprofile/")?.RoutePath ?? "/dynamic/poolprofile",
    [getRoute]
  );
  const remoteBatchHostBaseRoute = useMemo(
    () => getRoute("/dynamic/remotebatchhost/")?.RoutePath ?? "/dynamic/remotebatchhost",
    [getRoute]
  );
  const manualTestPayload = useMemo(
    () => getActionResultPayload(manualTestResult),
    [manualTestResult]
  );
  const manualTestDetailLines = useMemo(
    () => getActionResultDetailLines(manualTestResult),
    [manualTestResult]
  );
  const selectedManualTestHistoryEntry = useMemo(
    () =>
      manualTestHistory.find((entry) => entry.id === selectedManualTestHistoryId)
      ?? manualTestHistory[0]
      ?? null,
    [manualTestHistory, selectedManualTestHistoryId]
  );
  const selectedManualTestHistoryDetailLines = useMemo(
    () => getActionResultDetailLines(selectedManualTestHistoryEntry?.result),
    [selectedManualTestHistoryEntry]
  );
  const selectedRemoteHost = useMemo(
    () =>
      remoteHosts.find((host) => host?._id === selectedRemoteHostId)
      ?? remoteHosts[0]
      ?? null,
    [remoteHosts, selectedRemoteHostId]
  );
  const selectedAPIHost = useMemo(
    () =>
      apiHosts.find((host) => getAPIHostRowKey(host) === selectedAPIHostId)
      ?? apiHosts[0]
      ?? null,
    [apiHosts, selectedAPIHostId]
  );
  const selectedRemoteHostStartupFeed = useMemo(
    () => (selectedRemoteHost?._id ? remoteHostStartupFeeds[selectedRemoteHost._id] ?? null : null),
    [remoteHostStartupFeeds, selectedRemoteHost]
  );
  const filteredRemoteHosts = useMemo(
    () => (selectedRemoteHost ? [selectedRemoteHost] : []),
    [selectedRemoteHost]
  );
  const filteredAPIHosts = useMemo(
    () => (selectedAPIHost ? [selectedAPIHost] : []),
    [selectedAPIHost]
  );
  const selectedRemoteHostTasks = useMemo(() => {
    if (!selectedRemoteHost) {
      return [];
    }

    const seen = new Set();

    return activeTasks.filter((entry, index) => {
      const matchesHost =
        entry?.HostId === selectedRemoteHost?._id
        || (!!selectedRemoteHost?.CurrentJobId && entry?.JobId === selectedRemoteHost.CurrentJobId)
        || (!!selectedRemoteHost?.CurrentTaskId && entry?.TaskId === selectedRemoteHost.CurrentTaskId);

      if (!matchesHost) {
        return false;
      }

      const key = `${entry?.HostId ?? "host"}:${entry?.EventId ?? "event"}:${entry?.JobId ?? "job"}:${entry?.TaskId ?? index}`;
      if (seen.has(key)) {
        return false;
      }

      seen.add(key);
      return true;
    });
  }, [activeTasks, selectedRemoteHost]);
  const selectedAPIHostTasks = useMemo(() => {
    if (!selectedAPIHost) {
      return [];
    }

    const seen = new Set();

    return activeTasks.filter((entry, index) => {
      const matchesHost =
        entry?.HostId === selectedAPIHost?._id
        || (!!selectedAPIHost?.CurrentJobId && entry?.JobId === selectedAPIHost.CurrentJobId)
        || (!!selectedAPIHost?.CurrentTaskId && entry?.TaskId === selectedAPIHost.CurrentTaskId);

      if (!matchesHost) {
        return false;
      }

      const key = `${entry?.HostId ?? "host"}:${entry?.EventId ?? "event"}:${entry?.JobId ?? "job"}:${entry?.TaskId ?? index}`;
      if (seen.has(key)) {
        return false;
      }

      seen.add(key);
      return true;
    });
  }, [activeTasks, selectedAPIHost]);
  const selectedRemoteHostDebugLocalCommand = useMemo(
    () => (selectedRemoteHost?.IsDebugLocal ? getDebugLocalHostCommand(selectedRemoteHost) : ""),
    [selectedRemoteHost]
  );
  const selectedAPIHostDebugLocalCommand = useMemo(
    () => (selectedAPIHost?.IsDebugLocal ? getDebugLocalAPIHostCommand(selectedAPIHost) : ""),
    [selectedAPIHost]
  );
  const getEffectiveHostActionKey = useCallback(
    (host, operationName) => {
      if (!host?._id || !operationName) {
        return "";
      }

      if (hostActionKey) {
        return hostActionKey;
      }

      if (operationName === "StartRemoteBatchHost" && isRemoteHostStartupPending(host)) {
        const startupEventId = String(host?.StartupEventId ?? "").trim();
        const startupEventState = remoteHostStartupEventStates[host._id];

        if (!startupEventId || !startupEventState || startupEventState?.eventId !== startupEventId) {
          return `StartRemoteBatchHost:${host._id}`;
        }

        return startupEventState?.isActive === true
          ? `StartRemoteBatchHost:${host._id}`
          : `StartRemoteBatchHost:${host._id}`;
      }

      return "";
    },
    [hostActionKey, remoteHostStartupEventStates]
  );
  const isAnyActionRunning = !!(
    headerActionKey ||
    hostActionKey ||
    apiHostActionKey ||
    poolActionKey ||
    activeTaskActionKey ||
    scheduledActionKey ||
    activeManualTestActionKey
  );
  const isAutoRefreshBlocked = isManualTestDialogOpen || isRunningManualTest || isAnyActionRunning;
  const staleRemoteHosts = useMemo(
    () => remoteHosts.filter((host) => isRemoteHostStale(host)),
    [remoteHosts]
  );
  const staleAPIHosts = useMemo(
    () => apiHosts.filter((host) => isAPIHostStale(host)),
    [apiHosts]
  );
  const startupDiagnosticHosts = useMemo(
    () => remoteHosts.filter((host) => {
      const state = getRemoteHostStateLabel(host);
      return (
        host?.StartupFailureCount > 0
        || state === "Starting"
        || state === "Error"
        || state === "Draining"
        || !!host?.LastStatusDetail
      );
    }),
    [remoteHosts]
  );
  const apiDiagnosticHosts = useMemo(
    () => apiHosts.filter((host) => {
      const state = getAPIHostStateLabel(host);
      return (
        state === "Starting"
        || state === "Error"
        || state === "Stale"
        || !!host?.LastStatusDetail
        || !!host?.LastErrorMessage
      );
    }),
    [apiHosts]
  );
  const sectionDiagnosticRows = useMemo(
    () => [
      {
        key: "summary",
        label: "Summary",
        isLoading: loadingState.summary,
        error: sectionErrors.summary,
        itemCount: summary ? 1 : 0,
      },
      {
        key: "activeTasks",
        label: "Active Tasks",
        isLoading: loadingState.activeTasks,
        error: sectionErrors.activeTasks,
        itemCount: activeTasks.length,
      },
      {
        key: "remoteHosts",
        label: "Remote Hosts",
        isLoading: loadingState.remoteHosts,
        error: sectionErrors.remoteHosts,
        itemCount: remoteHosts.length,
      },
      {
        key: "apiHosts",
        label: "API Hosts",
        isLoading: loadingState.apiHosts,
        error: sectionErrors.apiHosts,
        itemCount: apiHosts.length,
      },
      {
        key: "background",
        label: "Background",
        isLoading: loadingState.background,
        error: sectionErrors.background,
        itemCount: backgroundProcesses.length,
      },
      {
        key: "scheduled",
        label: "Scheduled",
        isLoading: loadingState.scheduled,
        error: sectionErrors.scheduled,
        itemCount: scheduledWork.length,
      },
      {
        key: "pools",
        label: "Pools",
        isLoading: loadingState.pools,
        error: sectionErrors.pools,
        itemCount: poolProfiles.length,
      },
    ],
    [
      activeTasks.length,
      apiHosts.length,
      backgroundProcesses.length,
      loadingState.apiHosts,
      loadingState.activeTasks,
      loadingState.background,
      loadingState.pools,
      loadingState.remoteHosts,
      loadingState.scheduled,
      loadingState.summary,
      poolProfiles.length,
      remoteHosts.length,
      scheduledWork.length,
      sectionErrors.apiHosts,
      sectionErrors.activeTasks,
      sectionErrors.background,
      sectionErrors.pools,
      sectionErrors.remoteHosts,
      sectionErrors.scheduled,
      sectionErrors.summary,
      summary,
    ]
  );
  const diagnosticsOverview = useMemo(
    () => ({
      autoRefreshEnabled: isAutoRefreshEnabled,
      autoRefreshIntervalSeconds: AUTO_REFRESH_INTERVAL_SECONDS,
      autoRefreshBlocked: isAutoRefreshBlocked,
      lastRefreshDateTime,
      sectionErrorCount: sectionDiagnosticRows.filter((entry) => !!entry.error).length,
      staleHostCount: staleRemoteHosts.length + staleAPIHosts.length,
      staleRemoteHostCount: staleRemoteHosts.length,
      staleAPIHostCount: staleAPIHosts.length,
      drainingHostCount: remoteHosts.filter((host) => getRemoteHostStateLabel(host) === "Draining").length,
      apiHostCount: apiHosts.length,
      startupFailureCount: remoteHosts.reduce((total, host) => total + Number(host?.StartupFailureCount ?? 0), 0),
      apiHostErrorCount: apiHosts.filter((host) => getAPIHostStateLabel(host) === "Error").length,
      actionInFlight: isAnyActionRunning,
    }),
    [
      apiHosts,
      isAnyActionRunning,
      isAutoRefreshBlocked,
      isAutoRefreshEnabled,
      lastRefreshDateTime,
      remoteHosts,
      sectionDiagnosticRows,
      staleAPIHosts.length,
      staleRemoteHosts.length,
    ]
  );
  const currentBatchManagementPreferenceScopeKey = useMemo(
    () => `${coreContext?.Profile?.AppId ?? ""}:${coreContext?.Profile?.ClientId ?? "GLOBAL"}`,
    [coreContext?.Profile?.AppId, coreContext?.Profile?.ClientId]
  );

  useEffect(() => {
    genericMgrRef.current = genericMgr;
  }, [genericMgr]);

  useEffect(() => {
    remoteBatchHostMgrRef.current = remoteBatchHostMgr;
  }, [remoteBatchHostMgr]);

  useEffect(() => {
    apiEndpointBatchHostMgrRef.current = apiEndpointBatchHostMgr;
  }, [apiEndpointBatchHostMgr]);

  useEffect(() => {
    batchManagementPreferenceMgrRef.current = batchManagementPreferenceMgr;
  }, [batchManagementPreferenceMgr]);

  useEffect(() => {
    poolProfileMgrRef.current = poolProfileMgr;
  }, [poolProfileMgr]);

  useEffect(() => {
    eventMgrRef.current = eventMgr;
  }, [eventMgr]);

  useEffect(() => {
    if (remoteHosts.length === 0) {
      setSelectedRemoteHostId("");
      return;
    }

    if (!selectedRemoteHostId || !remoteHosts.some((host) => host?._id === selectedRemoteHostId)) {
      setSelectedRemoteHostId(remoteHosts[0]?._id ?? "");
    }
  }, [remoteHosts, selectedRemoteHostId]);

  useEffect(() => {
    if (apiHosts.length === 0) {
      setSelectedAPIHostId("");
      return;
    }

    if (!selectedAPIHostId || !apiHosts.some((host) => getAPIHostRowKey(host) === selectedAPIHostId)) {
      setSelectedAPIHostId(getAPIHostRowKey(apiHosts[0]));
    }
  }, [apiHosts, selectedAPIHostId]);

  useEffect(() => () => {
    remoteHostStreamControllersRef.current.forEach((controller) => {
      try {
        controller?.close?.();
      } catch (error) {
        console.error("Failed to close remote host stream controller:", error);
      }
    });
    remoteHostStreamControllersRef.current.clear();
  }, []);

  const closeRemoteHostStream = useCallback((hostId) => {
    if (!hostId) {
      return;
    }

    const existingController = remoteHostStreamControllersRef.current.get(hostId);
    if (existingController) {
      try {
        existingController.close?.();
      } catch (error) {
        console.error("Failed to close remote host stream:", error);
      }
      remoteHostStreamControllersRef.current.delete(hostId);
    }
  }, []);

  const mergeRemoteHostFromStream = useCallback((hostPayload) => {
    if (!hostPayload?._id) {
      return;
    }

    setRemoteHosts((current) => current.map((host) => (
      host?._id === hostPayload._id
        ? {
          ...host,
          ...hostPayload,
        }
        : host
    )));
  }, []);

  const appendRemoteHostStartupFeedEntry = useCallback((hostId, nextEntry, options = {}) => {
    if (!hostId || !nextEntry) {
      return;
    }

    setRemoteHostStartupFeeds((current) => {
      const existingFeed = current[hostId] ?? {
        eventId: "",
        isStreaming: false,
        isComplete: false,
        success: null,
        messages: [],
        lastUpdated: "",
      };

      const nextMessages = options?.replaceMessages
        ? [nextEntry]
        : [...existingFeed.messages, nextEntry].slice(-MAX_REMOTE_HOST_STARTUP_MESSAGES);

      return {
        ...current,
        [hostId]: {
          ...existingFeed,
          ...options,
          messages: nextMessages,
          lastUpdated: nextEntry.timestamp ?? new Date().toISOString(),
        },
      };
    });
  }, []);

  const setRemoteHostStartupEventState = useCallback((hostId, eventId, isActive, eventStatus = null) => {
    if (!hostId) {
      return;
    }

    setRemoteHostStartupEventStates((current) => ({
      ...current,
      [hostId]: {
        eventId: String(eventId ?? ""),
        isActive: !!isActive,
        eventStatus: eventStatus === null || eventStatus === undefined ? null : Number(eventStatus),
        checkedDateTime: new Date().toISOString(),
      },
    }));
  }, []);

  const getRemoteHostStartupEventStatus = useCallback(async (host) => {
    const hostId = String(host?._id ?? "").trim();
    const startupEventId = String(host?.StartupEventId ?? "").trim();

    if (!hostId || !startupEventId || !eventMgrRef.current?.single) {
      return {
        isActive: false,
        eventId: startupEventId,
        eventStatus: null,
      };
    }

    try {
      const response = await eventMgrRef.current.single(startupEventId);
      const normalizedResponse = getNormalizedSingleResult(response);
      const eventItem = normalizedResponse?.Items?.[0] ?? normalizedResponse?.Item ?? null;
      const eventStatus = eventItem?.EventStatus ?? eventItem?.Status ?? null;
      const isActive = !!eventItem && isStartupEventStatusActive(eventItem);

      setRemoteHostStartupEventState(hostId, startupEventId, isActive, eventStatus);

      return {
        isActive,
        eventId: startupEventId,
        eventStatus,
      };
    } catch (error) {
      console.warn("Failed to load remote host startup event state:", error);
      setRemoteHostStartupEventState(hostId, startupEventId, false, null);
      return {
        isActive: false,
        eventId: startupEventId,
        eventStatus: null,
      };
    }
  }, [setRemoteHostStartupEventState]);

  const handleRemoteHostStreamMessage = useCallback((hostId, message) => {
    if (!hostId || !message) {
      return;
    }

    const timestamp = new Date().toISOString();
    const payload = message?.OutputType === "object" ? message?.Message : null;
    const hostPayload = payload?.Host ?? null;
    const entryMessage = String(
      payload?.Message
      ?? message?.Message
      ?? ""
    ).trim();

    if (hostPayload?._id) {
      mergeRemoteHostFromStream(hostPayload);
    }

    const resolvedEventId = String(payload?.EventId ?? message?.EventId ?? "").trim();
    if (resolvedEventId) {
      const isCompletedMessage = message?.IsComplete === true;
      const resolvedEventStatus = isCompletedMessage
        ? (payload?.Success === false ? 0 : 3)
        : null;
      setRemoteHostStartupEventState(
        hostId,
        resolvedEventId,
        !isCompletedMessage,
        resolvedEventStatus
      );
    }

    if (!entryMessage) {
      return;
    }

    if (
      payload?.Success === false
      || String(message?.UIMessageType ?? "").trim().toLowerCase() === "error"
    ) {
      console.error("Remote host startup stream error.", {
        hostId,
        message,
        payload,
        host: hostPayload,
      });
    }

    appendRemoteHostStartupFeedEntry(hostId, {
      id: `${hostId}:${message?.EventId ?? "event"}:${timestamp}:${Math.random().toString(36).slice(2)}`,
      eventId: payload?.EventId ?? message?.EventId ?? "",
      message: entryMessage,
      stage: payload?.Stage ?? "",
      uiMessageType: message?.UIMessageType ?? "status",
      timestamp,
      outputType: message?.OutputType ?? "text",
    }, {
      eventId: payload?.EventId ?? message?.EventId ?? "",
      isStreaming: message?.IsComplete !== true,
      isComplete: message?.IsComplete === true,
      success: typeof payload?.Success === "boolean" ? payload.Success : undefined,
    });
  }, [appendRemoteHostStartupFeedEntry, mergeRemoteHostFromStream, setRemoteHostStartupEventState]);

  const setSectionLoading = useCallback((key, value) => {
    setLoadingState((current) => ({
      ...current,
      [key]: value,
    }));
  }, []);

  const setSectionError = useCallback((key, value) => {
    setSectionErrors((current) => ({
      ...current,
      [key]: value,
    }));
  }, []);

  const loadSummary = useCallback(async (options = {}) => {
    const showLoading = options?.showLoading !== false;
    if (showLoading) {
      setSectionLoading("summary", true);
      setSectionError("summary", "");
    }

    try {
      const response = await genericMgrRef.current?.RunOperation({
        operationName: "LoadBatchManagementSummary",
        isWarning: true,
      });
      const normalizedResponse = unwrapResult(response);

      if (!normalizedResponse?.Success) {
        setSectionError(
          "summary",
          getDisplayMessage(
            normalizedResponse,
            "Unable to load batch management summary."
          )
        );
      } else {
        setSectionError("summary", "");
      }

      setSummary(getItems(normalizedResponse)[0] ?? null);
    } catch (error) {
      console.error("Batch summary load failed:", error);
      setSectionError("summary", "Unable to load batch management summary.");
    } finally {
      if (showLoading) {
        setSectionLoading("summary", false);
      }
    }
  }, [setSectionError, setSectionLoading]);

  const loadActiveTasks = useCallback(async (options = {}) => {
    const showLoading = options?.showLoading !== false;
    if (showLoading) {
      setSectionLoading("activeTasks", true);
      setSectionError("activeTasks", "");
    }

    try {
      const response = await genericMgrRef.current?.RunOperation({
        operationName: "LoadBatchActiveTasks",
        isWarning: true,
      });
      const normalizedResponse = unwrapResult(response);

      if (!normalizedResponse?.Success) {
        setSectionError(
          "activeTasks",
          getDisplayMessage(normalizedResponse, "Unable to load active tasks.")
        );
      } else {
        setSectionError("activeTasks", "");
      }

      setActiveTasks(getItems(normalizedResponse));
    } catch (error) {
      console.error("Batch active tasks load failed:", error);
      setSectionError("activeTasks", "Unable to load active tasks.");
    } finally {
      if (showLoading) {
        setSectionLoading("activeTasks", false);
      }
    }
  }, [setSectionError, setSectionLoading]);

  const loadRemoteHosts = useCallback(async (options = {}) => {
    const showLoading = options?.showLoading !== false;
    if (showLoading) {
      setSectionLoading("remoteHosts", true);
      setSectionError("remoteHosts", "");
    }

    try {
      const response = await genericMgrRef.current?.RunOperation({
        operationName: "LoadBatchRemoteHosts",
        isWarning: true,
      });
      const normalizedResponse = unwrapResult(response);
      const operationItems = getItems(normalizedResponse);

      if (normalizedResponse?.Success) {
        setSectionError("remoteHosts", "");
        setRemoteHosts(operationItems);
        return;
      }

      const fallbackResponse = await remoteBatchHostMgrRef.current?.Query({
        model: "x => x.IsActive == true && x.IsDeleted == false",
      });
      const normalizedFallbackResponse = unwrapResult(fallbackResponse);
      const fallbackItems = getItems(normalizedFallbackResponse);

      if (normalizedFallbackResponse?.Success) {
        setSectionError("remoteHosts", "");
        setRemoteHosts(fallbackItems);
        console.warn(
          "LoadBatchRemoteHosts failed; falling back to direct RemoteBatchHost query.",
          normalizedResponse
        );
        return;
      }

      setSectionError(
        "remoteHosts",
        getDisplayMessage(
          normalizedResponse,
          getDisplayMessage(normalizedFallbackResponse, "Unable to load remote hosts.")
        )
      );
      setRemoteHosts([]);
    } catch (error) {
      console.error("Batch remote host load failed:", error);
      setSectionError("remoteHosts", "Unable to load remote hosts.");
    } finally {
      if (showLoading) {
        setSectionLoading("remoteHosts", false);
      }
    }
  }, [setSectionError, setSectionLoading]);

  const loadAPIHosts = useCallback(async (options = {}) => {
    const showLoading = options?.showLoading !== false;
    if (showLoading) {
      setSectionLoading("apiHosts", true);
      setSectionError("apiHosts", "");
    }

    try {
      const response = await genericMgrRef.current?.RunOperation({
        operationName: "LoadBatchAPIEndpointHosts",
        isWarning: true,
      });
      const normalizedResponse = unwrapResult(response);
      const operationItems = getItems(normalizedResponse);

      if (normalizedResponse?.Success) {
        setSectionError("apiHosts", "");
        setAPIHosts(operationItems);
        return;
      }

      const fallbackResponse = await apiEndpointBatchHostMgrRef.current?.Query({
        model: "x => x.IsActive == true && x.IsDeleted == false",
      });
      const normalizedFallbackResponse = unwrapResult(fallbackResponse);
      const fallbackItems = getItems(normalizedFallbackResponse);

      if (normalizedFallbackResponse?.Success) {
        setSectionError("apiHosts", "");
        setAPIHosts(fallbackItems);
        console.warn(
          "LoadBatchAPIEndpointHosts failed; falling back to direct APIEndpointBatchHost query.",
          normalizedResponse
        );
        return;
      }

      setSectionError(
        "apiHosts",
        getDisplayMessage(
          normalizedResponse,
          getDisplayMessage(normalizedFallbackResponse, "Unable to load API hosts.")
        )
      );
      setAPIHosts([]);
    } catch (error) {
      console.error("Batch API host load failed:", error);
      setSectionError("apiHosts", "Unable to load API hosts.");
    } finally {
      if (showLoading) {
        setSectionLoading("apiHosts", false);
      }
    }
  }, [setSectionError, setSectionLoading]);

  const loadPools = useCallback(async (options = {}) => {
    const showLoading = options?.showLoading !== false;
    if (showLoading) {
      setSectionLoading("pools", true);
      setSectionError("pools", "");
    }

    try {
      const response = await genericMgrRef.current?.RunOperation({
        operationName: "LoadBatchPools",
        isWarning: true,
      });
      const normalizedResponse = unwrapResult(response);
      const operationItems = getItems(normalizedResponse);

      if (normalizedResponse?.Success && operationItems.length > 0) {
        setSectionError("pools", "");
        setPoolProfiles(sortPoolProfiles(operationItems));
        return;
      }

      const fallbackResponse = await poolProfileMgrRef.current?.Query({
        model: "x => x.IsDeleted == false",
      });
      const normalizedFallbackResponse = unwrapResult(fallbackResponse);
      const fallbackItems = getItems(normalizedFallbackResponse);

      if (normalizedFallbackResponse?.Success) {
        setSectionError("pools", "");
        setPoolProfiles(sortPoolProfiles(fallbackItems));
        if (!normalizedResponse?.Success) {
          console.warn(
            "LoadBatchPools failed; falling back to direct PoolProfile query.",
            normalizedResponse
          );
        }
        return;
      }

      setSectionError(
        "pools",
        getDisplayMessage(
          normalizedResponse,
          getDisplayMessage(normalizedFallbackResponse, "Unable to load pool profiles.")
        )
      );
      setPoolProfiles([]);
    } catch (error) {
      console.error("Batch pool load failed:", error);
      setSectionError("pools", "Unable to load pool profiles.");
    } finally {
      if (showLoading) {
        setSectionLoading("pools", false);
      }
    }
  }, [setSectionError, setSectionLoading]);

  const loadBackgroundProcesses = useCallback(async (options = {}) => {
    const showLoading = options?.showLoading !== false;
    if (showLoading) {
      setSectionLoading("background", true);
      setSectionError("background", "");
    }

    try {
      const response = await genericMgrRef.current?.RunOperation({
        operationName: "LoadBatchBackgroundProcesses",
        isWarning: true,
      });
      const normalizedResponse = unwrapResult(response);

      if (!normalizedResponse?.Success) {
        setSectionError(
          "background",
          getDisplayMessage(normalizedResponse, "Unable to load background events.")
        );
      } else {
        setSectionError("background", "");
      }

      setBackgroundProcesses(getItems(normalizedResponse));
    } catch (error) {
      console.error("Batch background load failed:", error);
      setSectionError("background", "Unable to load background events.");
    } finally {
      if (showLoading) {
        setSectionLoading("background", false);
      }
    }
  }, [setSectionError, setSectionLoading]);

  const loadScheduledWork = useCallback(async (options = {}) => {
    const showLoading = options?.showLoading !== false;
    if (showLoading) {
      setSectionLoading("scheduled", true);
      setSectionError("scheduled", "");
    }

    try {
      const response = await genericMgrRef.current?.RunOperation({
        operationName: "LoadBatchScheduledWork",
        isWarning: true,
      });
      const normalizedResponse = unwrapResult(response);

      if (!normalizedResponse?.Success) {
        setSectionError(
          "scheduled",
          getDisplayMessage(normalizedResponse, "Unable to load scheduled work.")
        );
      } else {
        setSectionError("scheduled", "");
      }

      setScheduledWork(getItems(normalizedResponse));
    } catch (error) {
      console.error("Batch scheduled work load failed:", error);
      setSectionError("scheduled", "Unable to load scheduled work.");
    } finally {
      if (showLoading) {
        setSectionLoading("scheduled", false);
      }
    }
  }, [setSectionError, setSectionLoading]);

  const loadBatchManagementPreference = useCallback(async () => {
    const currentClientId = coreContext?.Profile?.ClientId;
    const currentAppId = coreContext?.Profile?.AppId;

    if (!batchManagementPreferenceMgrRef.current || !currentAppId) {
      setBatchManagementPreference(null);
      setManualTestHistory([]);
      setIsAutoRefreshEnabled(true);
      setHasLoadedBatchManagementPreference(true);
      setLoadedBatchManagementPreferenceScopeKey(currentBatchManagementPreferenceScopeKey);
      return;
    }

    try {
      const searchModel = currentClientId
        ? "x => x.IsActive == true && x.IsDeleted == false && x.PreferenceKey == preferenceKey && x.ClientId == clientId"
        : "x => x.IsActive == true && x.IsDeleted == false && x.PreferenceKey == preferenceKey && x.ClientId == null";

      const response = await batchManagementPreferenceMgrRef.current.search({
        searchModel,
        params: {
          preferenceKey: BATCH_MANAGEMENT_PREFERENCE_KEY,
          clientId: currentClientId,
        },
      });

      const preferenceItem = getItems(response)[0] ?? null;
      const manualTestHistoryItems = Array.isArray(preferenceItem?.ManualTestHistory) && preferenceItem.ManualTestHistory.length > 0
        ? normalizeManualTestHistory(preferenceItem.ManualTestHistory)
        : parseManualTestHistoryJson(preferenceItem?.ManualTestHistoryJson);

      setBatchManagementPreference(preferenceItem);
      setManualTestHistory(manualTestHistoryItems);
      setIsAutoRefreshEnabled(preferenceItem?.AutoRefreshEnabled ?? true);
    } catch (error) {
      console.warn("Batch management preference load failed:", error);
      setBatchManagementPreference(null);
      setManualTestHistory([]);
      setIsAutoRefreshEnabled(true);
    } finally {
      setHasLoadedBatchManagementPreference(true);
      setLoadedBatchManagementPreferenceScopeKey(currentBatchManagementPreferenceScopeKey);
    }
  }, [
    coreContext?.Profile?.AppId,
    coreContext?.Profile?.ClientId,
    currentBatchManagementPreferenceScopeKey,
  ]);

  const saveBatchManagementPreference = useCallback(async () => {
    if (
      !hasLoadedBatchManagementPreference
      || loadedBatchManagementPreferenceScopeKey !== currentBatchManagementPreferenceScopeKey
      || !batchManagementPreferenceMgrRef.current
    ) {
      return;
    }

    const currentAppId = coreContext?.Profile?.AppId;
    if (!currentAppId) {
      return;
    }

    const nextPreferenceItem = {
      ...(batchManagementPreference ?? {}),
      AppId: currentAppId,
      ClientId: coreContext?.Profile?.ClientId ?? null,
      PreferenceKey: BATCH_MANAGEMENT_PREFERENCE_KEY,
      AutoRefreshEnabled: !!isAutoRefreshEnabled,
      ManualTestHistory: toManualTestHistoryPreferenceItems(manualTestHistory),
      ManualTestHistoryJson: null,
    };

    try {
      const response = await batchManagementPreferenceMgrRef.current.saveItem(nextPreferenceItem);
      const savedPreferenceItem = getItems(response)[0] ?? null;
      if (savedPreferenceItem?._id && savedPreferenceItem._id !== batchManagementPreference?._id) {
        setBatchManagementPreference(savedPreferenceItem);
      }
    } catch (error) {
      console.warn("Batch management preference save failed:", error);
    }
  }, [
    batchManagementPreference,
    coreContext?.Profile?.AppId,
    coreContext?.Profile?.ClientId,
    hasLoadedBatchManagementPreference,
    isAutoRefreshEnabled,
    loadedBatchManagementPreferenceScopeKey,
    manualTestHistory,
    currentBatchManagementPreferenceScopeKey,
  ]);

  const refreshAllSections = useCallback(async (options = {}) => {
    if (refreshInFlightRef.current) {
      return false;
    }

    refreshInFlightRef.current = true;

    try {
      const showLoading = options?.showLoading !== false;
      await Promise.all([
        loadSummary({ showLoading }),
        loadActiveTasks({ showLoading }),
        loadRemoteHosts({ showLoading }),
        loadAPIHosts({ showLoading }),
        loadPools({ showLoading }),
        loadBackgroundProcesses({ showLoading }),
        loadScheduledWork({ showLoading }),
      ]);
      setLastRefreshDateTime(new Date().toISOString());
      return true;
    } finally {
      refreshInFlightRef.current = false;
    }
  }, [loadAPIHosts, loadActiveTasks, loadBackgroundProcesses, loadPools, loadRemoteHosts, loadScheduledWork, loadSummary]);

  useEffect(() => {
    void refreshAllSections();
  }, [refreshAllSections]);

  useEffect(() => {
    void loadBatchManagementPreference();
  }, [loadBatchManagementPreference]);

  useEffect(() => {
    void saveBatchManagementPreference();
  }, [saveBatchManagementPreference]);

  useEffect(() => {
    if (!isAutoRefreshEnabled) {
      return undefined;
    }

    const intervalId = window.setInterval(() => {
      if (!isAutoRefreshBlocked) {
        void refreshAllSections({ showLoading: false });
      }
    }, AUTO_REFRESH_INTERVAL_SECONDS * 1000);

    return () => window.clearInterval(intervalId);
  }, [isAutoRefreshBlocked, isAutoRefreshEnabled, refreshAllSections]);

  const resumeRemoteHostStartupStream = useCallback(
    async (host) => {
      const hostId = host?._id;
      if (!hostId || remoteHostStreamControllersRef.current.has(hostId)) {
        return;
      }

      try {
        const hubController = await remoteBatchHostMgrRef.current?.GetStream({
          operationName: "StartRemoteBatchHost",
          id: hostId,
          type: "sse",
        });

        if (!hubController?.Success || typeof hubController.onMessage !== "function") {
          return;
        }

        remoteHostStreamControllersRef.current.set(hostId, hubController);
        hubController.onMessage((message) => handleRemoteHostStreamMessage(hostId, message));

        void (async () => {
          try {
            const hubCompletion = await hubController.untilDone;
            if (hubCompletion?.reason !== "done") {
              return;
            }

            const completionMessage = hubCompletion?.data ?? null;
            const completionPayload = completionMessage?.OutputType === "object" ? completionMessage?.Message : null;

            if (completionPayload?.Host?._id) {
              mergeRemoteHostFromStream(completionPayload.Host);
            }

            if (completionPayload?.Success === false) {
              setLastActionResult(buildRemoteHostStartupFailureResult({
                hostId,
                fallbackMessage: "Remote host startup failed.",
                payload: completionPayload,
                host: completionPayload?.Host ?? null,
                eventId: completionPayload?.EventId ?? completionMessage?.EventId ?? "",
              }));
            }

            setRemoteHostStartupEventState(
              hostId,
              completionPayload?.EventId ?? completionMessage?.EventId ?? host?.StartupEventId ?? "",
              false,
              completionPayload?.Success === false ? 0 : 3
            );

            await Promise.all([
              loadSummary({ showLoading: false }),
              loadActiveTasks({ showLoading: false }),
              loadRemoteHosts({ showLoading: false }),
            ]);
            setLastRefreshDateTime(new Date().toISOString());
          } catch (streamError) {
            console.error("Recovered remote host startup stream failed:", streamError);
          } finally {
            remoteHostStreamControllersRef.current.delete(hostId);
          }
        })();
      } catch (error) {
        console.error("Failed to resume remote host startup stream:", error);
      }
    },
    [handleRemoteHostStreamMessage, loadActiveTasks, loadRemoteHosts, loadSummary, mergeRemoteHostFromStream, setRemoteHostStartupEventState]
  );

  useEffect(() => {
    if (!selectedRemoteHost?._id) {
      return;
    }

    const hostId = selectedRemoteHost._id;
    let isCancelled = false;

    const stopRecoveredStartup = () => {
      closeRemoteHostStream(hostId);
      setRemoteHostStartupEventState(hostId, selectedRemoteHost?.StartupEventId ?? "", false);
      setRemoteHostStartupFeeds((current) => {
        const existingFeed = current[hostId];
        if (!existingFeed?.isStreaming) {
          return current;
        }

        return {
          ...current,
          [hostId]: {
            ...existingFeed,
            isStreaming: false,
            isComplete: true,
            success: getRemoteHostStateLabel(selectedRemoteHost) === "Online" ? true : existingFeed.success,
            lastUpdated: new Date().toISOString(),
          },
        };
      });
    };

    const recoverStartup = async () => {
      if (!isRemoteHostStartupResumable(selectedRemoteHost)) {
        stopRecoveredStartup();
        return;
      }

      const startupEventStatus = await getRemoteHostStartupEventStatus(selectedRemoteHost);
      if (isCancelled) {
        return;
      }

      if (!startupEventStatus?.isActive) {
        stopRecoveredStartup();
        return;
      }

      const recoveredEntry = getRecoveredRemoteHostStartupEntry(selectedRemoteHost);
      if (recoveredEntry) {
        setRemoteHostStartupFeeds((current) => {
          const existingFeed = current[hostId];
          if (existingFeed?.messages?.length) {
            return {
              ...current,
              [hostId]: {
                ...existingFeed,
                isStreaming: true,
                isComplete: false,
              },
            };
          }

          return {
            ...current,
            [hostId]: {
              eventId: startupEventStatus?.eventId ?? "",
              isStreaming: true,
              isComplete: false,
              success: null,
              messages: [recoveredEntry],
              lastUpdated: recoveredEntry.timestamp,
            },
          };
        });
      }

      void resumeRemoteHostStartupStream(selectedRemoteHost);
    };

    void recoverStartup();

    return () => {
      isCancelled = true;
    };
  }, [closeRemoteHostStream, getRemoteHostStartupEventStatus, resumeRemoteHostStartupStream, selectedRemoteHost, setRemoteHostStartupEventState]);

  useEffect(() => {
    if (!manualTestOptions.length) {
      setSelectedManualTestKey("azure-batch");
      return;
    }

    if (!manualTestOptions.some((option) => option.key === selectedManualTestKey)) {
      setSelectedManualTestKey(manualTestOptions[0].key);
    }
  }, [manualTestOptions, selectedManualTestKey]);

  useEffect(() => {
    if (!manualTestHistory.length) {
      setSelectedManualTestHistoryId("");
      return;
    }

    if (!manualTestHistory.some((entry) => entry.id === selectedManualTestHistoryId)) {
      setSelectedManualTestHistoryId(manualTestHistory[0].id);
    }
  }, [manualTestHistory, selectedManualTestHistoryId]);

  useEffect(() => {
    if (lastActionResult?.DisplayMessage) {
      setIsLastActionDialogOpen(true);
    }
  }, [lastActionResult]);

  const openManualTestDialog = useCallback((manualTestKey = "azure-batch", launcherKey = "") => {
    if (isRunningManualTest) {
      return;
    }

    setSelectedManualTestKey(manualTestKey);
    setManualTestLauncherKey(launcherKey);
    setManualTestResult(null);
    setIsManualTestDialogOpen(true);
  }, [isRunningManualTest]);

  const closeManualTestDialog = useCallback(() => {
    setIsManualTestDialogOpen(false);
  }, []);

  const closeLastActionDialog = useCallback(() => {
    setIsLastActionDialogOpen(false);
  }, []);

  const confirmCloseLastActionDialog = useCallback(async () => {
    setIsLastActionDialogOpen(false);
    return { Success: true };
  }, []);

  const handleHeaderAction = useCallback(
    async (operationName) => {
      setHeaderActionKey(operationName);
      setLastActionResult(null);

      try {
        const response = await genericMgrRef.current?.RunOperation({
          operationName,
          isWarning: true,
        });

        setLastActionResult(unwrapResult(response));
        await refreshAllSections();
        setLastRefreshDateTime(new Date().toISOString());
      } catch (error) {
        console.error(`Batch management header action '${operationName}' failed:`, error);
        setLastActionResult({
          Success: false,
          DisplayMessage: "Batch management action failed.",
          MessageDetails: [String(error?.message ?? error ?? "Unknown error")],
        });
      } finally {
        setHeaderActionKey("");
      }
    },
    [refreshAllSections]
  );

  const handleRunManualTest = useCallback(async () => {
    if (!selectedManualTest || isRunningManualTest) {
      return;
    }

    const nextManualTestActionKey = manualTestLauncherKey || `manual-test:${selectedManualTest.key}`;
    setIsRunningManualTest(true);
    setActiveManualTestActionKey(nextManualTestActionKey);
    setIsManualTestDialogOpen(false);
    setManualTestResult(null);

    try {
      const response = selectedManualTest.isHostTest
        ? await remoteBatchHostMgrRef.current?.RunOperation({
          operationName: selectedManualTest.operationName,
          id: selectedManualTest.targetId,
          isItem: true,
          isWarning: true,
        })
        : selectedManualTest.isPoolTest
          ? await poolProfileMgrRef.current?.RunOperation({
            operationName: selectedManualTest.operationName,
            id: selectedManualTest.targetId,
            isItem: true,
            isWarning: true,
          })
        : await genericMgrRef.current?.RunOperation({
          operationName: selectedManualTest.operationName,
          isWarning: true,
        });

      const normalizedResponse = unwrapResult(response);
      setManualTestResult(normalizedResponse);
      const nextHistoryEntry = createManualTestHistoryEntry(selectedManualTest, normalizedResponse);
      setManualTestHistory((current) => [nextHistoryEntry, ...current].slice(0, MAX_MANUAL_TEST_HISTORY_ITEMS));
      setSelectedManualTestHistoryId(nextHistoryEntry.id);
      void refreshAllSections({ showLoading: false });
    } catch (error) {
      console.error(`Manual test '${selectedManualTest.operationName}' failed:`, error);
      const failedResult = {
        Success: false,
        DisplayMessage: "Manual test failed.",
        MessageDetails: [String(error?.message ?? error ?? "Unknown error")],
      };
      setManualTestResult(failedResult);
      const nextHistoryEntry = createManualTestHistoryEntry(selectedManualTest, failedResult);
      setManualTestHistory((current) => [nextHistoryEntry, ...current].slice(0, MAX_MANUAL_TEST_HISTORY_ITEMS));
      setSelectedManualTestHistoryId(nextHistoryEntry.id);
    } finally {
      setIsRunningManualTest(false);
      setActiveManualTestActionKey("");
    }
  }, [isRunningManualTest, manualTestLauncherKey, refreshAllSections, selectedManualTest]);

  const handlePoolManualTest = useCallback(
    async (poolProfile) => {
      const poolId = poolProfile?._id;
      if (!poolId || isRunningManualTest) {
        return;
      }

      const actionKey = `pool-check:${poolId}`;
      const manualTestOption = {
        key: `pool:${poolId}`,
        operationName: "CheckAzureBatchPoolStatus",
        label: `${poolProfile?.Name ?? "Pool Profile"}${poolProfile?.Version ? ` v${poolProfile.Version}` : ""}`,
        scopeLabel: poolProfile?.IsDefault ? "Default Pool" : "Pool Profile",
        targetId: poolId,
      };

      setIsRunningManualTest(true);
      setActiveManualTestActionKey(actionKey);
      setLastActionResult(null);
      setManualTestResult(null);

      try {
        const response = await poolProfileMgrRef.current?.RunOperation({
          operationName: manualTestOption.operationName,
          id: poolId,
          isItem: true,
          isWarning: true,
        });

        const normalizedResponse = unwrapResult(response);
        setManualTestResult(normalizedResponse);
        setLastActionResult(normalizedResponse);

        const nextHistoryEntry = createManualTestHistoryEntry(manualTestOption, normalizedResponse);
        setManualTestHistory((current) => [nextHistoryEntry, ...current].slice(0, MAX_MANUAL_TEST_HISTORY_ITEMS));
        setSelectedManualTestHistoryId(nextHistoryEntry.id);

        await Promise.all([
          loadSummary({ showLoading: false }),
          loadPools({ showLoading: false }),
        ]);
        setLastRefreshDateTime(new Date().toISOString());
      } catch (error) {
        console.error("Pool check failed:", error);
        const failedResult = {
          Success: false,
          DisplayMessage: "Pool check failed.",
          MessageDetails: [String(error?.message ?? error ?? "Unknown error")],
        };
        setManualTestResult(failedResult);
        setLastActionResult(failedResult);

        const nextHistoryEntry = createManualTestHistoryEntry(manualTestOption, failedResult);
        setManualTestHistory((current) => [nextHistoryEntry, ...current].slice(0, MAX_MANUAL_TEST_HISTORY_ITEMS));
        setSelectedManualTestHistoryId(nextHistoryEntry.id);
      } finally {
        setIsRunningManualTest(false);
        setActiveManualTestActionKey("");
      }
    },
    [isRunningManualTest, loadPools, loadSummary]
  );

  const handleEnsureReadyHostAction = useCallback(
    async (hostId) => {
      if (!hostId) {
        return;
      }

      const currentHost = remoteHosts.find((entry) => entry?._id === hostId);
      if (hostActionKey || isRemoteHostStartupResumable(currentHost)) {
        return;
      }

      const nextActionKey = `StartRemoteBatchHost:${hostId}`;
      setHostActionKey(nextActionKey);
      setLastActionResult(null);
      closeRemoteHostStream(hostId);

      setRemoteHostStartupFeeds((current) => ({
        ...current,
        [hostId]: {
          eventId: "",
          isStreaming: true,
          isComplete: false,
          success: null,
          messages: [],
          lastUpdated: new Date().toISOString(),
        },
      }));

      let hubController = null;

      try {
        hubController = await remoteBatchHostMgrRef.current?.GetStream({
          operationName: "StartRemoteBatchHost",
          id: hostId,
          type: "sse",
        });

        if (hubController?.Success && typeof hubController.onMessage === "function") {
          remoteHostStreamControllersRef.current.set(hostId, hubController);
          hubController.onMessage((message) => handleRemoteHostStreamMessage(hostId, message));
        }

        const response = await remoteBatchHostMgrRef.current?.RunOperation({
          operationName: "StartRemoteBatchHost",
          id: hostId,
          isItem: true,
          isWarning: true,
          processorType: 2,
        });

        const normalizedResponse = unwrapResult(response);
        const isFailedToQueue = normalizedResponse && normalizedResponse.Success === false;
        const queuedEventId = String(normalizedResponse?.EventId ?? "").trim();

        if (isFailedToQueue) {
          const failedResult = buildRemoteHostStartupFailureResult({
            hostId,
            fallbackMessage: "Remote host startup failed to queue.",
            result: normalizedResponse,
            eventId: normalizedResponse?.EventId ?? "",
          });
          setLastActionResult(failedResult);
          appendRemoteHostStartupFeedEntry(hostId, {
            id: `${hostId}:queue-failed:${Date.now()}`,
            eventId: normalizedResponse?.EventId ?? "",
            message: failedResult.DisplayMessage,
            stage: "error",
            uiMessageType: "error",
            timestamp: new Date().toISOString(),
            outputType: "text",
          }, {
            eventId: normalizedResponse?.EventId ?? "",
            isStreaming: false,
            isComplete: true,
            success: false,
          });
          closeRemoteHostStream(hostId);
          return;
        }

        if (queuedEventId) {
          setRemoteHostStartupEventState(hostId, queuedEventId, true, 1);
          setRemoteHosts((current) => current.map((entry) => (
            entry?._id === hostId
              ? {
                ...entry,
                Status: 1,
                StartupEventId: queuedEventId,
                LastStartupRequestedDateTime: new Date().toISOString(),
              }
              : entry
          )));
        }

        if (hubController?.Success && hubController?.untilDone) {
          let hubCompletion = null;

          try {
            hubCompletion = await hubController.untilDone;
          } catch (streamError) {
            console.error("Remote host startup stream failed:", streamError);
            setLastActionResult({
              Success: false,
              DisplayMessage: "Remote host startup stream failed.",
              MessageDetails: [String(streamError?.message ?? streamError ?? "Unknown stream error")],
            });
            appendRemoteHostStartupFeedEntry(hostId, {
              id: `${hostId}:stream-error:${Date.now()}`,
              eventId: "",
              message: String(streamError?.message ?? streamError ?? "Unknown stream error"),
              stage: "error",
              uiMessageType: "error",
              timestamp: new Date().toISOString(),
              outputType: "text",
            }, {
              isStreaming: false,
              isComplete: true,
              success: false,
            });
            setRemoteHostStartupEventState(hostId, queuedEventId, false, 0);
            return;
          } finally {
            remoteHostStreamControllersRef.current.delete(hostId);
          }

          const completionMessage = hubCompletion?.data ?? null;
          const completionPayload = completionMessage?.OutputType === "object" ? completionMessage?.Message : null;

          if (completionPayload?.Host?._id) {
            mergeRemoteHostFromStream(completionPayload.Host);
          }

          if (completionPayload?.Success === false) {
            setLastActionResult(buildRemoteHostStartupFailureResult({
              hostId,
              fallbackMessage: "Remote host startup failed.",
              payload: completionPayload,
              host: completionPayload?.Host ?? null,
              eventId: completionPayload?.EventId ?? completionMessage?.EventId ?? "",
            }));
          }

          setRemoteHostStartupEventState(
            hostId,
            completionPayload?.EventId ?? hubCompletion?.data?.EventId ?? queuedEventId,
            false,
            completionPayload?.Success === false ? 0 : 3
          );
        } else {
          if (normalizedResponse?.Success === false) {
            setLastActionResult(buildRemoteHostStartupFailureResult({
              hostId,
              fallbackMessage: "Remote host startup failed.",
              result: normalizedResponse,
              eventId: normalizedResponse?.EventId ?? "",
            }));
          }
        }

        await Promise.all([
          loadSummary({ showLoading: false }),
          loadActiveTasks({ showLoading: false }),
          loadRemoteHosts({ showLoading: false }),
        ]);
        setLastRefreshDateTime(new Date().toISOString());
      } catch (error) {
        console.error("Ensure Ready failed:", error);
        setLastActionResult(buildRemoteHostStartupFailureResult({
          hostId,
          fallbackMessage: "Failed to start remote batch host.",
          result: {
            Success: false,
            DisplayMessage: "Failed to start remote batch host.",
            MessageDetails: [String(error?.message ?? error ?? "Unknown error")],
          },
        }));
        appendRemoteHostStartupFeedEntry(hostId, {
          id: `${hostId}:action-error:${Date.now()}`,
          eventId: "",
          message: String(error?.message ?? error ?? "Unknown error"),
          stage: "error",
          uiMessageType: "error",
          timestamp: new Date().toISOString(),
          outputType: "text",
        }, {
          isStreaming: false,
          isComplete: true,
          success: false,
        });
        setRemoteHostStartupEventState(hostId, "", false, 0);
      } finally {
        closeRemoteHostStream(hostId);
        setHostActionKey("");
      }
    },
    [
      appendRemoteHostStartupFeedEntry,
      closeRemoteHostStream,
      handleRemoteHostStreamMessage,
      hostActionKey,
      loadActiveTasks,
      loadRemoteHosts,
      loadSummary,
      mergeRemoteHostFromStream,
      remoteHosts,
      setRemoteHostStartupEventState,
    ]
  );

  const handleHostAction = useCallback(
    async (hostId, operationName) => {
      if (!hostId) {
        return;
      }

      const currentHost = remoteHosts.find((entry) => entry?._id === hostId);
      if (operationName !== "StartRemoteBatchHost" && isRemoteHostStartupPending(currentHost)) {
        return;
      }

      if (operationName === "StartRemoteBatchHost") {
        await handleEnsureReadyHostAction(hostId);
        return;
      }

      const nextActionKey = `${operationName}:${hostId}`;
      setHostActionKey(nextActionKey);
      setLastActionResult(null);

      try {
        const response = await remoteBatchHostMgrRef.current?.RunOperation({
          operationName,
          id: hostId,
          isItem: true,
          isWarning: true,
        });

        setLastActionResult(unwrapResult(response));
        await Promise.all([
          loadSummary({ showLoading: false }),
          loadActiveTasks({ showLoading: false }),
          loadRemoteHosts({ showLoading: false }),
        ]);
        setLastRefreshDateTime(new Date().toISOString());
      } catch (error) {
        console.error(`Remote host action '${operationName}' failed:`, error);
        setLastActionResult({
          Success: false,
          DisplayMessage: "Remote host action failed.",
          MessageDetails: [String(error?.message ?? error ?? "Unknown error")],
        });
      } finally {
        setHostActionKey("");
      }
    },
    [handleEnsureReadyHostAction, loadActiveTasks, loadRemoteHosts, loadSummary, remoteHosts]
  );

  const handleAPIHostAction = useCallback(
    async (hostOrId, operationName) => {
      if (!hostOrId || !operationName) {
        return;
      }

      const resolvedHost = typeof hostOrId === "object" && hostOrId !== null
        ? hostOrId
        : apiHosts.find((host) =>
          host?._id === hostOrId
          || getAPIHostRowKey(host) === hostOrId);
      const operationItem = resolvedHost ?? { _id: hostOrId };
      const operationId = getGuidOperationId(operationItem?._id);
      const rowKey = resolvedHost ? getAPIHostRowKey(resolvedHost) : String(hostOrId ?? "").trim();
      if (!rowKey) {
        return;
      }

      const nextActionKey = `${operationName}:${rowKey}`;
      setAPIHostActionKey(nextActionKey);
      setLastActionResult(null);

      try {
        const response = await genericMgrRef.current?.RunOperation({
          operationName,
          ...(operationId ? { id: operationId } : {}),
          item: operationItem,
          isItem: true,
          isWarning: true,
        });

        setLastActionResult(unwrapResult(response));
        await Promise.all([
          loadSummary({ showLoading: false }),
          loadActiveTasks({ showLoading: false }),
          loadAPIHosts({ showLoading: false }),
        ]);
        setLastRefreshDateTime(new Date().toISOString());
      } catch (error) {
        console.error(`API host action '${operationName}' failed:`, error);
        setLastActionResult({
          Success: false,
          DisplayMessage: "API host action failed.",
          MessageDetails: [String(error?.message ?? error ?? "Unknown error")],
        });
      } finally {
        setAPIHostActionKey("");
      }
    },
    [apiHosts, loadAPIHosts, loadActiveTasks, loadSummary]
  );

  const handleCopyDebugLocalCommand = useCallback(async () => {
    if (!selectedRemoteHost?._id || !selectedRemoteHostDebugLocalCommand) {
      return;
    }

    try {
      if (navigator?.clipboard?.writeText instanceof Function) {
        await navigator.clipboard.writeText(selectedRemoteHostDebugLocalCommand);
        setCopiedDebugLocalCommandHostId(selectedRemoteHost._id);
        window.setTimeout(() => {
          setCopiedDebugLocalCommandHostId((current) => (current === selectedRemoteHost._id ? "" : current));
        }, 2000);
      }
    } catch (error) {
      console.error("Failed to copy debug-local host command.", error);
    }
  }, [selectedRemoteHost, selectedRemoteHostDebugLocalCommand]);

  const handleCopyAPIHostDebugLocalCommand = useCallback(async () => {
    const apiHostCommandKey = selectedAPIHost ? getAPIHostRowKey(selectedAPIHost) : "";
    if (!apiHostCommandKey || !selectedAPIHostDebugLocalCommand) {
      return;
    }

    try {
      if (navigator?.clipboard?.writeText instanceof Function) {
        await navigator.clipboard.writeText(selectedAPIHostDebugLocalCommand);
        setCopiedAPIHostDebugLocalCommandKey(apiHostCommandKey);
        window.setTimeout(() => {
          setCopiedAPIHostDebugLocalCommandKey((current) => (current === apiHostCommandKey ? "" : current));
        }, 2000);
      }
    } catch (error) {
      console.error("Failed to copy debug-local API host command.", error);
    }
  }, [selectedAPIHost, selectedAPIHostDebugLocalCommand]);

  const handleOpenBackgroundEvent = useCallback(
    (eventId) => {
      if (!eventId) {
        return;
      }

      navigate(`${eventManageBaseRoute}/${eventId}`);
    },
    [eventManageBaseRoute, navigate]
  );

  const handlePoolAction = useCallback(
    async (poolProfile, operationName) => {
      const poolId = poolProfile?._id;
      if (!poolId || !operationName) {
        return;
      }

      const nextActionKey = `${operationName}:${poolId}`;
      setPoolActionKey(nextActionKey);
      setLastActionResult(null);

      try {
        const response = await poolProfileMgrRef.current?.RunOperation({
          operationName,
          id: poolId,
          item: poolProfile,
          isItem: true,
          isWarning: true,
        });

        setLastActionResult(unwrapResult(response));
        await Promise.all([
          loadSummary({ showLoading: false }),
          loadPools({ showLoading: false }),
          loadActiveTasks({ showLoading: false }),
        ]);
        setLastRefreshDateTime(new Date().toISOString());
      } catch (error) {
        console.error(`Pool action '${operationName}' failed:`, error);
        setLastActionResult({
          Success: false,
          DisplayMessage: "Pool action failed.",
          MessageDetails: [String(error?.message ?? error ?? "Unknown error")],
        });
      } finally {
        setPoolActionKey("");
      }
    },
    [loadActiveTasks, loadPools, loadSummary]
  );

  const handleOpenPoolProfile = useCallback(
    (poolProfileId) => {
      if (!poolProfileId) {
        return;
      }

      navigate(`${poolProfileBaseRoute}/${poolProfileId}`);
    },
    [navigate, poolProfileBaseRoute]
  );

  const handleOpenRemoteBatchHost = useCallback(
    (hostId) => {
      if (!hostId) {
        return;
      }

      navigate(`${remoteBatchHostBaseRoute}/${hostId}`);
    },
    [navigate, remoteBatchHostBaseRoute]
  );

  const handleActiveTaskAction = useCallback(
    async (entry, actionKey) => {
      if (!entry || !actionKey) {
        return;
      }

      const nextActionKey = getActiveTaskActionKey(entry, actionKey);
      setActiveTaskActionKey(nextActionKey);
      setLastActionResult(null);

      try {
        if (actionKey === "open-event") {
          handleOpenBackgroundEvent(entry?.EventId);
          return;
        }

        if (actionKey === "open-host") {
          if (isAPIEndpointHostTask(entry)) {
            setActiveTab("api-hosts");
            setSelectedAPIHostId(entry?.HostId ?? "");
            return;
          }

          handleOpenRemoteBatchHost(entry?.HostId);
          return;
        }

        if (actionKey === "cancel-event") {
          if (!entry?.EventId || !window.confirm("Cancel this background event?")) {
            return;
          }

          const response = await eventMgrRef.current?.RunOperation({
            operationName: "SetEventStatus",
            queryParams: {
              eventid: entry.EventId,
              status: 4,
              msg: "Cancelled from Batch Management.",
            },
            isWarning: true,
          });

          setLastActionResult(unwrapResult(response));
          await Promise.all([
            loadSummary({ showLoading: false }),
            loadActiveTasks({ showLoading: false }),
            loadBackgroundProcesses({ showLoading: false }),
            loadScheduledWork({ showLoading: false }),
            loadAPIHosts({ showLoading: false }),
          ]);
          setLastRefreshDateTime(new Date().toISOString());
          return;
        }

        if (actionKey === "stop-host") {
          const confirmMessage = isAPIEndpointHostTask(entry)
            ? "Drop this API endpoint host?"
            : "Stop this remote batch host?";
          if (!entry?.HostId || !window.confirm(confirmMessage)) {
            return;
          }

          if (isAPIEndpointHostTask(entry)) {
            await handleAPIHostAction(entry.HostId, "StopAPIEndpointBatchHost");
          } else {
            await handleHostAction(entry.HostId, "StopRemoteBatchHost");
          }
          setLastRefreshDateTime(new Date().toISOString());
          return;
        }

        if (actionKey === "terminate-job") {
          if (!entry?.JobId || !window.confirm("Terminate this Azure Batch job?")) {
            return;
          }

          const response = await genericMgrRef.current?.RunOperation({
            operationName: "ManageAzureBatchJob",
            queryParams: {
              action: "terminate",
              jobId: entry.JobId,
              hostId: entry.HostId ?? "",
              eventid: entry.EventId ?? "",
            },
            isWarning: true,
          });

          setLastActionResult(unwrapResult(response));
          await Promise.all([
            loadSummary({ showLoading: false }),
            loadActiveTasks({ showLoading: false }),
            loadRemoteHosts({ showLoading: false }),
            loadAPIHosts({ showLoading: false }),
          ]);
          setLastRefreshDateTime(new Date().toISOString());
          return;
        }

        if (actionKey === "terminate-task") {
          if (!entry?.JobId || !entry?.TaskId || !window.confirm("Terminate this Azure Batch task?")) {
            return;
          }

          const response = await genericMgrRef.current?.RunOperation({
            operationName: "ManageAzureBatchTask",
            queryParams: {
              action: "terminate",
              jobId: entry.JobId,
              taskId: entry.TaskId,
              hostId: entry.HostId ?? "",
              eventid: entry.EventId ?? "",
            },
            isWarning: true,
          });

          setLastActionResult(unwrapResult(response));
          await Promise.all([
            loadSummary({ showLoading: false }),
            loadActiveTasks({ showLoading: false }),
            loadRemoteHosts({ showLoading: false }),
            loadAPIHosts({ showLoading: false }),
          ]);
          setLastRefreshDateTime(new Date().toISOString());
        }
      } catch (error) {
        console.error(`Active task action '${actionKey}' failed:`, error);
        setLastActionResult({
          Success: false,
          DisplayMessage: "Active task action failed.",
          MessageDetails: [String(error?.message ?? error ?? "Unknown error")],
        });
      } finally {
        setActiveTaskActionKey("");
      }
    },
    [
      handleAPIHostAction,
      handleHostAction,
      handleOpenBackgroundEvent,
      handleOpenRemoteBatchHost,
      loadAPIHosts,
      loadActiveTasks,
      loadBackgroundProcesses,
      loadRemoteHosts,
      loadScheduledWork,
      loadSummary,
    ]
  );

  const handleScheduledAction = useCallback(
    async (entry, actionKey) => {
      if (!entry?.EventId || !actionKey) {
        return;
      }

      const nextActionKey = getScheduledActionKey(entry, actionKey);
      setScheduledActionKey(nextActionKey);
      setLastActionResult(null);

      try {
        if (actionKey === "open-event") {
          handleOpenBackgroundEvent(entry.EventId);
          return;
        }

        if (actionKey === "pause") {
          const response = await eventMgrRef.current?.RunOperation({
            operationName: "SetEventStatus",
            queryParams: {
              eventid: entry.EventId,
              status: 6,
            },
            isWarning: true,
          });

          setLastActionResult(unwrapResult(response));
        } else if (actionKey === "run-now") {
          const response = await eventMgrRef.current?.RunOperation({
            operationName: "RunEventNow",
            queryParams: {
              eventid: entry.EventId,
            },
            isWarning: true,
          });

          setLastActionResult(unwrapResult(response));
        } else if (actionKey === "resume") {
          const response = await eventMgrRef.current?.RunOperation({
            operationName: "SetEventStatus",
            queryParams: {
              eventid: entry.EventId,
              status: 5,
            },
            isWarning: true,
          });

          setLastActionResult(unwrapResult(response));
        } else if (actionKey === "cancel") {
          if (!window.confirm("Cancel this scheduled event?")) {
            return;
          }

          const response = await eventMgrRef.current?.RunOperation({
            operationName: "SetEventStatus",
            queryParams: {
              eventid: entry.EventId,
              status: 4,
              msg: "Cancelled from Batch Management.",
            },
            isWarning: true,
          });

          setLastActionResult(unwrapResult(response));
        }

        await Promise.all([
          loadSummary({ showLoading: false }),
          loadActiveTasks({ showLoading: false }),
          loadBackgroundProcesses({ showLoading: false }),
          loadScheduledWork({ showLoading: false }),
        ]);
        setLastRefreshDateTime(new Date().toISOString());
      } catch (error) {
        console.error(`Scheduled action '${actionKey}' failed:`, error);
        setLastActionResult({
          Success: false,
          DisplayMessage: "Scheduled action failed.",
          MessageDetails: [String(error?.message ?? error ?? "Unknown error")],
        });
      } finally {
        setScheduledActionKey("");
      }
    },
    [
      handleOpenBackgroundEvent,
      loadActiveTasks,
      loadBackgroundProcesses,
      loadScheduledWork,
      loadSummary,
    ]
  );


  if (!canViewBatchManagement) {
    return (
      <div className="batch-management-screen">
        <div className="batch-management-empty">
          <IconControl iconKey="server" />
          <div className="title">Batch management is unavailable.</div>
          <div className="description">
            You do not have permission to view this operational screen.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="batch-management-screen">
      <div className="batch-management-header">
        <div>
          <div className="eyebrow">System Management</div>
          <h1>Batch Management</h1>
          <p>
            Monitor Azure Batch health, remote execution hosts, and background
            processing from one screen.
          </p>
        </div>
        <div className="batch-management-header-actions">
          <div className="batch-management-refresh-status">
            <span>
              Auto Refresh: {isAutoRefreshEnabled ? `On (${AUTO_REFRESH_INTERVAL_SECONDS}s)` : "Off"}
            </span>
            <span>
              Last Refresh: {lastRefreshDateTime ? formatDateTime(lastRefreshDateTime) : "Not yet loaded"}
            </span>
            {isAutoRefreshEnabled && isAutoRefreshBlocked ? (
              <span>Auto refresh paused while an action is running.</span>
            ) : null}
          </div>
          <ButtonControl value="Refresh" type="standard" onClick={() => void refreshAllSections()} />
          <ButtonControl
            value={isAutoRefreshEnabled ? "Pause Auto Refresh" : "Resume Auto Refresh"}
            type="standard"
            onClick={() => setIsAutoRefreshEnabled((current) => !current)}
          />
          <ButtonControl
            value={headerActionKey === "CheckAzureBatchStatus" ? "Checking..." : "Check Azure Batch"}
            type="standard"
            disabled={!!headerActionKey}
            onClick={() => void handleHeaderAction("CheckAzureBatchStatus")}
          />
        </div>
      </div>

      <div className="batch-management-content">
        <DialogControl
          className="batch-management-dialog"
          openDialog={isLastActionDialogOpen}
          title={lastActionResult?.Success ? "Operation Completed" : "Operation Error"}
          okText="Close"
          excludeCancel={true}
          onOk={confirmCloseLastActionDialog}
          onCancel={closeLastActionDialog}
          onX={closeLastActionDialog}
        >
          <div
            className={`batch-management-message ${
              lastActionResult?.Success ? "is-ok" : "is-error"
            }`}
          >
            <div className="message-title">{lastActionResult?.DisplayMessage ?? "Operation result"}</div>
            {lastActionPayload ? (
              <div className="message-meta">
                {lastActionPayload?.TestName ? (
                  <span>{lastActionPayload.TestName}</span>
                ) : null}
                {getPayloadStatusLabel(lastActionPayload) ? (
                  <span>Status: {getPayloadStatusLabel(lastActionPayload)}</span>
                ) : null}
                {lastActionPayload?.CheckedDateTime ? (
                  <span>Checked: {formatDateTime(lastActionPayload.CheckedDateTime)}</span>
                ) : null}
              </div>
            ) : null}
            {lastActionDetailLines.slice(0, 8).map((entry) => (
              <div key={entry} className="message-detail">
                {entry}
              </div>
            ))}
          </div>
        </DialogControl>

        <div className="batch-management-tabs">
          {TABS.map((tab) => (
            <button
              key={tab.key}
              className={`batch-management-tab ${activeTab === tab.key ? "is-active" : ""}`}
              type="button"
              onClick={() => setActiveTab(tab.key)}
            >
              {tab.label}
            </button>
          ))}
        </div>

      <DialogControl
        className="batch-management-dialog"
        openDialog={isManualTestDialogOpen}
        title="Manual Tests"
        okText="Run Test"
        cancelText="Close"
        disableOk={!selectedManualTest || isRunningManualTest}
        onOk={() => {
          void handleRunManualTest();
        }}
        onCancel={closeManualTestDialog}
      >
        <div className="batch-management-test-dialog">
          <div className="batch-management-test-list">
            {manualTestOptions.map((option) => (
              <button
                key={option.key}
                type="button"
                className={`batch-management-test-option ${
                  selectedManualTest?.key === option.key ? "is-active" : ""
                }`}
                onClick={() => {
                  setSelectedManualTestKey(option.key);
                  setManualTestResult(null);
                }}
              >
                <div className="test-option-title">{option.label}</div>
                <div className="test-option-meta">
                  {option.scopeLabel}
                  {option.status ? ` | ${option.status}` : ""}
                  {option.poolId ? ` | ${option.poolId}` : ""}
                </div>
              </button>
            ))}
          </div>
          <div className="batch-management-test-result">
            {selectedManualTest ? (
              <>
                <div className="test-result-title">{selectedManualTest.label}</div>
                <div className="test-result-description">{selectedManualTest.description}</div>
                {selectedManualTest.endpoint ? (
                  <div className="test-result-meta">Endpoint: {selectedManualTest.endpoint}</div>
                ) : null}
                {selectedManualTest.poolId ? (
                  <div className="test-result-meta">Pool Id: {selectedManualTest.poolId}</div>
                ) : null}
              </>
            ) : null}

            {manualTestResult?.DisplayMessage ? (
              <div
                className={`batch-management-message ${
                  manualTestResult?.Success ? "is-ok" : "is-error"
                }`}
              >
                <div className="message-title">{manualTestResult.DisplayMessage}</div>
                {manualTestPayload ? (
                  <div className="message-meta">
                    {manualTestPayload?.TestName ? (
                      <span>{manualTestPayload.TestName}</span>
                    ) : null}
                    {getPayloadStatusLabel(manualTestPayload) ? (
                      <span>Status: {getPayloadStatusLabel(manualTestPayload)}</span>
                    ) : null}
                    {manualTestPayload?.CheckedDateTime ? (
                      <span>Checked: {formatDateTime(manualTestPayload.CheckedDateTime)}</span>
                    ) : null}
                  </div>
                ) : null}
                {manualTestDetailLines.map((entry) => (
                  <div key={entry} className="message-detail">
                    {entry}
                  </div>
                ))}
                {renderCapabilityResults(manualTestPayload?.CapabilityResults)}
              </div>
            ) : (
              <div className="batch-management-test-empty">
                Run the selected manual test to see the result here.
              </div>
            )}
          </div>
        </div>
      </DialogControl>

      <div className={`batch-management-scroll-content ${activeTab === "remote-hosts" || activeTab === "api-hosts" ? "is-remote-hosts" : ""}`}>
      {activeTab === "overview" ? (
        <div className="batch-management-overview">
          {sectionErrors.summary ? (
            <div className="batch-management-message is-error">{sectionErrors.summary}</div>
          ) : null}
          <div className="batch-management-card-grid">
            <div className="batch-management-card">
              <div className="card-label">Azure Batch</div>
              <div className={`card-value ${getStatusToneClass(summary?.AzureBatchStatus)}`}>
                {loadingState.summary ? "Loading..." : (summary?.AzureBatchStatus ?? "Unknown")}
              </div>
              <div className="card-meta">
                {loadingState.summary
                  ? "Loading Azure Batch status."
                  : (summary?.AzureBatchMessage ?? "No Azure Batch status has been loaded yet.")}
              </div>
            </div>
            <div className="batch-management-card">
              <div className="card-label">Configured Pools</div>
              <div className="card-value">{loadingState.summary ? "..." : (summary?.ConfiguredPoolCount ?? 0)}</div>
              <div className="card-meta">
                {loadingState.pools
                  ? "Loading pool profiles."
                  : defaultPoolProfile
                    ? `Default: ${defaultPoolProfile?.Name ?? defaultPoolProfile?.PoolId ?? "Configured"}`
                    : "No default pool profile is configured."}
              </div>
            </div>
            <div className="batch-management-card">
              <div className="card-label">Remote Hosts</div>
              <div className="card-value">{loadingState.summary ? "..." : (summary?.RemoteHostCount ?? 0)}</div>
              <div className="card-meta">
                {loadingState.summary
                  ? "Loading remote host summary."
                  : `${summary?.OnlineRemoteHostCount ?? 0} online, ${summary?.StartingRemoteHostCount ?? 0} starting, ${summary?.ErrorRemoteHostCount ?? 0} error.`}
              </div>
            </div>
            <div className="batch-management-card">
              <div className="card-label">API Hosts</div>
              <div className="card-value">{loadingState.summary ? "..." : (summary?.APIHostCount ?? 0)}</div>
              <div className="card-meta">
                {loadingState.summary
                  ? "Loading API host summary."
                  : `${summary?.OnlineAPIHostCount ?? 0} online, ${summary?.StartingAPIHostCount ?? 0} starting, ${summary?.ErrorAPIHostCount ?? 0} error.`}
              </div>
            </div>
            <div className="batch-management-card">
              <div className="card-label">Background Work</div>
              <div className="card-value">{loadingState.summary ? "..." : (summary?.BackgroundInProgressCount ?? 0)}</div>
              <div className="card-meta">
                {loadingState.summary
                  ? "Loading background summary."
                  : `${summary?.BackgroundScheduledCount ?? 0} scheduled, ${summary?.BackgroundFailedLast24HoursCount ?? 0} failed in the last 24 hours.`}
              </div>
            </div>
            <div className="batch-management-card">
              <div className="card-label">Active Tasks</div>
              <div className="card-value">{loadingState.summary ? "..." : (summary?.ActiveTaskCount ?? 0)}</div>
              <div className="card-meta">
                Combined active host and background task estimate for the current scope.
              </div>
            </div>
            <div className="batch-management-card">
              <div className="card-label">Last Remote Heartbeat</div>
              <div className="card-value">
                {loadingState.summary ? "Loading..." : formatDateTime(summary?.LastRemoteHeartbeatDateTime)}
              </div>
              <div className="card-meta">
                {loadingState.summary
                  ? "Loading heartbeat status."
                  : `${summary?.StaleRemoteHostCount ?? 0} host(s) currently have a stale heartbeat.`}
              </div>
            </div>
            <div className="batch-management-card">
              <div className="card-label">Last API Heartbeat</div>
              <div className="card-value">
                {loadingState.summary ? "Loading..." : formatDateTime(summary?.LastAPIHostHeartbeatDateTime)}
              </div>
              <div className="card-meta">
                {loadingState.summary
                  ? "Loading API heartbeat status."
                  : `${summary?.StaleAPIHostCount ?? 0} API host(s) currently have a stale heartbeat.`}
              </div>
            </div>
          </div>

          <div className="batch-management-panel-grid">
            <section className="batch-management-panel">
              <div className="panel-header">
                <h2>Remote Host Snapshot</h2>
                <span>{loadingState.remoteHosts ? "Loading..." : `${remoteHosts.length} host(s)`}</span>
              </div>
              {sectionErrors.remoteHosts ? (
                <div className="table-empty">{sectionErrors.remoteHosts}</div>
              ) : loadingState.remoteHosts ? (
                <div className="batch-management-section-loading"><Loading /></div>
              ) : (
              <div className="mini-list">
                {remoteHosts.slice(0, 5).map((host) => (
                  <div key={host?._id} className="mini-list-row">
                    <div>
                      <div className="mini-list-title">
                        {host?.Name ?? "Remote Batch Host"}
                      </div>
                      <div className="mini-list-detail">
                        {host?.ClientShortName ?? "DEFAULT"} |{" "}
                        {host?.Endpoint ?? "No endpoint"}
                      </div>
                    </div>
                    <div className={`mini-list-status ${getStatusToneClass(getRemoteHostStateLabel(host))}`}>
                      {getRemoteHostStateLabel(host)}
                    </div>
                  </div>
                ))}
                {remoteHosts.length === 0 ? (
                  <div className="mini-list-empty">
                    No remote host registrations were found.
                  </div>
                ) : null}
              </div>
              )}
            </section>

            <section className="batch-management-panel">
              <div className="panel-header">
                <h2>API Host Snapshot</h2>
                <span>{loadingState.apiHosts ? "Loading..." : `${apiHosts.length} host(s)`}</span>
              </div>
              {sectionErrors.apiHosts ? (
                <div className="table-empty">{sectionErrors.apiHosts}</div>
              ) : loadingState.apiHosts ? (
                <div className="batch-management-section-loading"><Loading /></div>
              ) : (
              <div className="mini-list">
                {apiHosts.slice(0, 5).map((host) => (
                  <div key={getAPIHostRowKey(host)} className="mini-list-row">
                    <div>
                      <div className="mini-list-title">
                        {host?.APIName ?? "API Endpoint"} | {host?.DeploymentKey ?? "deployment"}
                      </div>
                      <div className="mini-list-detail">
                        {getDestinationStatusLabel(host?.DeploymentStatus)} | {host?.PublicBaseUrl ?? host?.Endpoint ?? "No URL"}
                      </div>
                    </div>
                    <div className={`mini-list-status ${getStatusToneClass(getAPIHostStateLabel(host))}`}>
                      {getAPIHostStateLabel(host)}
                    </div>
                  </div>
                ))}
                {apiHosts.length === 0 ? (
                  <div className="mini-list-empty">
                    No API host registrations were found.
                  </div>
                ) : null}
              </div>
              )}
            </section>

            <section className="batch-management-panel">
              <div className="panel-header">
                <h2>Background Snapshot</h2>
                <span>{loadingState.background ? "Loading..." : `${backgroundSnapshotEntries.length} item(s)`}</span>
              </div>
              {sectionErrors.background ? (
                <div className="table-empty">{sectionErrors.background}</div>
              ) : loadingState.background ? (
                <div className="batch-management-section-loading"><Loading /></div>
              ) : (
              <div className="mini-list">
                {backgroundSnapshotEntries.map((entry) => (
                  <div key={entry?._id ?? entry?.EventId} className="mini-list-row">
                    <div>
                      <div className="mini-list-title">
                        {entry?.DisplayName ?? "Background Event"}
                      </div>
                      <div className="mini-list-detail">
                        {PROCESSOR_TYPE_LABELS[Number(entry?.ProcessorType ?? 0)] ??
                          "Unknown"}{" "}
                        |{" "}
                        {EVENT_STATUS_LABELS[Number(entry?.EventStatus ?? -1)] ??
                          "Unknown"}
                      </div>
                    </div>
                    <div className="mini-list-status">
                      {formatDateTime(entry?.RunDate ?? entry?.CreatedDate)}
                    </div>
                  </div>
                ))}
                {backgroundSnapshotEntries.length === 0 ? (
                  <div className="mini-list-empty">
                    No background activity was found in the last 7 days.
                  </div>
                ) : null}
              </div>
              )}
            </section>
          </div>
        </div>
      ) : null}

      {activeTab === "active-tasks" ? (
        <div className="batch-management-table-card is-remote-hosts">
          <div className="table-header">
            <h2>Active Tasks</h2>
            <span>{loadingState.activeTasks ? "Loading..." : `${activeTasks.length} task(s)`}</span>
          </div>
          {sectionErrors.activeTasks ? (
            <div className="table-empty">{sectionErrors.activeTasks}</div>
          ) : loadingState.activeTasks ? (
            <div className="batch-management-section-loading"><Loading /></div>
          ) : (
          <div className="batch-management-table-scroll">
            <table className="batch-management-table is-active-tasks">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Scope</th>
                  <th>Processor</th>
                  <th>Source Status</th>
                  <th>Runtime Status</th>
                  <th className="batch-management-col-pool">Pool</th>
                  <th className="batch-management-col-job">Job</th>
                  <th>Task</th>
                  <th>Started</th>
                  <th>Completed</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {activeTasks.map((entry, index) => (
                  <tr key={`${entry?.HostId ?? "host"}:${entry?.EventId ?? "event"}:${entry?.JobId ?? "job"}:${entry?.TaskId ?? index}`}>
                    <td>
                      <div>{entry?.DisplayName ?? "Task"}</div>
                      {entry?.Details ? (
                        <div className="table-secondary">{entry.Details}</div>
                      ) : null}
                    </td>
                    <td>{entry?.ScopeType ?? "-"}</td>
                    <td>{entry?.ProcessorLabel ?? "-"}</td>
                    <td>
                      <span className={`table-status-chip ${getStatusToneClass(entry?.SourceStatus)}`}>
                        {entry?.SourceStatus ?? "-"}
                      </span>
                    </td>
                    <td>
                      <span className={`table-status-chip ${getStatusToneClass(entry?.RuntimeStatus)}`}>
                        {entry?.RuntimeStatus ?? "-"}
                      </span>
                    </td>
                    <td
                      className="is-monospace batch-management-col-pool"
                      title={entry?.PoolId ?? "-"}
                    >
                      {entry?.PoolId ?? "-"}
                    </td>
                    <td
                      className="is-monospace batch-management-col-job"
                      title={entry?.JobId ?? "-"}
                    >
                      {entry?.JobId ?? "-"}
                    </td>
                    <td className="is-monospace">
                      {entry?.TaskId ?? entry?.NodeId ?? "-"}
                    </td>
                    <td>{formatDateTime(entry?.StartedDateTime ?? entry?.CreatedDateTime)}</td>
                    <td>{formatDateTime(entry?.CompletedDateTime)}</td>
                    <td>
                      <div className="row-actions">
                        {entry?.EventId ? (
                          <>
                            <button
                              className="row-action-button"
                              type="button"
                              disabled={activeTaskActionKey !== "" && activeTaskActionKey !== getActiveTaskActionKey(entry, "open-event")}
                              onClick={() => void handleActiveTaskAction(entry, "open-event")}
                            >
                              {renderRowActionButtonContent(
                                "Open Event",
                                activeTaskActionKey === getActiveTaskActionKey(entry, "open-event")
                              )}
                            </button>
                            {Number(entry?.ProcessorType ?? 0) !== 8 ? (
                              <button
                                className="row-action-button"
                                type="button"
                                disabled={activeTaskActionKey !== "" && activeTaskActionKey !== getActiveTaskActionKey(entry, "cancel-event")}
                                onClick={() => void handleActiveTaskAction(entry, "cancel-event")}
                              >
                                {renderRowActionButtonContent(
                                  "Cancel Event",
                                  activeTaskActionKey === getActiveTaskActionKey(entry, "cancel-event")
                                )}
                              </button>
                            ) : null}
                          </>
                        ) : null}
                        {entry?.HostId ? (
                          <>
                            <button
                              className="row-action-button"
                              type="button"
                              disabled={activeTaskActionKey !== "" && activeTaskActionKey !== getActiveTaskActionKey(entry, "open-host")}
                              onClick={() => void handleActiveTaskAction(entry, "open-host")}
                            >
                              {renderRowActionButtonContent(
                                "Open Host",
                                activeTaskActionKey === getActiveTaskActionKey(entry, "open-host")
                              )}
                            </button>
                            {entry?.JobId ? (
                              <button
                                className="row-action-button"
                                type="button"
                                disabled={activeTaskActionKey !== "" && activeTaskActionKey !== getActiveTaskActionKey(entry, "terminate-job")}
                                onClick={() => void handleActiveTaskAction(entry, "terminate-job")}
                              >
                                {renderRowActionButtonContent(
                                  "Terminate Job",
                                  activeTaskActionKey === getActiveTaskActionKey(entry, "terminate-job")
                                )}
                              </button>
                            ) : null}
                            {entry?.JobId && entry?.TaskId ? (
                              <button
                                className="row-action-button"
                                type="button"
                                disabled={activeTaskActionKey !== "" && activeTaskActionKey !== getActiveTaskActionKey(entry, "terminate-task")}
                                onClick={() => void handleActiveTaskAction(entry, "terminate-task")}
                              >
                                {renderRowActionButtonContent(
                                  "Cancel Task",
                                  activeTaskActionKey === getActiveTaskActionKey(entry, "terminate-task")
                                )}
                              </button>
                            ) : null}
                            <button
                              className="row-action-button"
                              type="button"
                              disabled={activeTaskActionKey !== "" && activeTaskActionKey !== getActiveTaskActionKey(entry, "stop-host")}
                              onClick={() => void handleActiveTaskAction(entry, "stop-host")}
                            >
                              {renderRowActionButtonContent(
                                "Stop Host",
                                activeTaskActionKey === getActiveTaskActionKey(entry, "stop-host")
                              )}
                            </button>
                          </>
                        ) : null}
                        {!entry?.EventId && !entry?.HostId ? (
                          <span className="table-secondary">No actions</span>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {activeTasks.length === 0 ? (
              <div className="table-empty">No active tasks are currently visible.</div>
            ) : null}
          </div>
          )}
        </div>
      ) : null}

      {activeTab === "remote-hosts" ? (
        <div className="batch-management-table-card is-remote-hosts">
          <div className="table-header">
            <h2>Remote Hosts</h2>
            <span>{loadingState.remoteHosts ? "Loading..." : `${remoteHosts.length} host(s)`}</span>
          </div>
          {sectionErrors.remoteHosts ? (
            <div className="table-empty">{sectionErrors.remoteHosts}</div>
          ) : loadingState.remoteHosts ? (
            <div className="batch-management-section-loading"><Loading /></div>
          ) : (
          <>
            {remoteHosts.length > 0 ? (
              <div className="batch-management-filter-row">
                <label className="batch-management-filter-label" htmlFor="remote-host-selector">
                  Remote Host
                </label>
                <select
                  id="remote-host-selector"
                  className="batch-management-inline-select"
                  value={selectedRemoteHost?._id ?? ""}
                  onChange={(event) => setSelectedRemoteHostId(event.target.value)}
                >
                  {remoteHosts.map((host) => (
                    <option key={host?._id} value={host?._id ?? ""}>
                      {`${host?.Name ?? "Remote Batch Host"}${host?.IsDebugLocal ? " (Debug Local)" : ""}`}
                    </option>
                  ))}
                </select>
                <span className="batch-management-filter-meta">
                  Showing 1 selected host out of {remoteHosts.length}
                </span>
              </div>
            ) : null}
            <div className="batch-management-table-scroll">
              <table className="batch-management-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Route</th>
                    <th>Status</th>
                    <th>Endpoint</th>
                    <th>Batch</th>
                    <th>Heartbeat / Uptime</th>
                    <th>Lifecycle Detail</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRemoteHosts.map((host) => (
                    <tr
                      key={host?._id}
                      className={selectedRemoteHost?._id === host?._id ? "is-selected-row" : ""}
                      onClick={() => setSelectedRemoteHostId(host?._id ?? "")}
                    >
                    <td>
                      <div>{host?.Name ?? "Remote Batch Host"}</div>
                      <div className="table-secondary">
                        {host?.ClientShortName ?? "DEFAULT"}
                        {host?.IsDebugLocal ? " | Debug Local" : ""}
                        {host?.Environment ? ` | ${host.Environment}` : ""}
                      </div>
                    </td>
                    <td>
                      <div>{getRemoteHostRouteLabel(host)}</div>
                      <div className="table-secondary">
                        {host?.HostInstanceId ? `Instance ${host.HostInstanceId}` : "No active instance"}
                      </div>
                    </td>
                    <td>
                      <span className={`table-status-chip ${getStatusToneClass(getRemoteHostStateLabel(host))}`}>
                        {getRemoteHostDisplayStateLabel(host)}
                      </span>
                    </td>
                    <td>
                      <div>{host?.Endpoint ?? "-"}</div>
                      <div className="table-secondary">
                        {host?.Endpoint ? "Direct host endpoint" : "No endpoint registered"}
                      </div>
                    </td>
                    <td>
                      <div className="is-monospace">{host?.PoolId ?? "-"}</div>
                      <div className="table-secondary is-monospace">
                        Job: {host?.CurrentJobId ?? "-"} | Task: {host?.CurrentTaskId ?? "-"}
                      </div>
                    </td>
                    <td>
                      <div>{formatDateTime(host?.LastHeartbeatDateTime)}</div>
                      <div className="table-secondary">
                        {getRemoteHostUptimeLabel(host) || "Not started"}
                      </div>
                    </td>
                    <td>
                      <div>{formatDateTime(host?.LastErrorDateTime ?? host?.LastStartupRequestedDateTime)}</div>
                      <div className="table-secondary">
                        {getRemoteHostLifecycleDetail(host)}
                      </div>
                    </td>
                    <td>
                      <div className="row-actions">
                        {REMOTE_HOST_ACTIONS.map((action) => {
                          const actionKey = `${action.operationName ?? action.label}:${host?._id}`;
                          const effectiveActionKey = getEffectiveHostActionKey(host, action.operationName);
                          const isHostActionLoading = !action.isManualTest && effectiveActionKey === actionKey;
                          const isManualTestLoading = action.isManualTest && activeManualTestActionKey === `host-test:${host?._id}`;
                          return (
                            <button
                              key={action.operationName ?? action.label}
                              className="row-action-button"
                              type="button"
                              disabled={
                                action.isManualTest
                                  ? (isRunningManualTest || isRemoteHostStartupPending(host))
                                  : action.operationName
                                    ? (
                                      effectiveActionKey !== ""
                                      || (action.operationName !== "StartRemoteBatchHost" && isRemoteHostStartupPending(host))
                                    )
                                    : false
                              }
                              onClick={(event) => {
                                event.stopPropagation();

                                if (isHostActionLoading || isManualTestLoading) {
                                  return;
                                }

                                if (action.isManualTest) {
                                  openManualTestDialog(`host:${host?._id}`, `host-test:${host?._id}`);
                                  return;
                                }

                                void handleHostAction(host?._id, action.operationName);
                              }}
                            >
                              {isManualTestLoading ? (
                                <span className="row-action-content is-loading">
                                  <span className="row-action-spinner" />
                                  <span>{action.label}</span>
                                </span>
                              ) : isHostActionLoading ? (
                                <span className="row-action-content is-loading">
                                  <span className="row-action-spinner" />
                                  <span>{action.label}</span>
                                </span>
                              ) : (
                                action.label
                              )}
                            </button>
                          );
                        })}
                      </div>
                    </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {filteredRemoteHosts.length === 0 ? (
                <div className="table-empty">No remote host registrations were found.</div>
              ) : null}
            </div>
            {selectedRemoteHost ? (
              <div className="batch-management-detail-panel-container">
              <div className="batch-management-detail-panel">
              <div className="detail-panel-header">
                <div>
                  <h3>{selectedRemoteHost?.Name ?? "Remote Batch Host"}</h3>
                  <div className="detail-panel-subtitle">
                    {selectedRemoteHost?.ClientShortName ?? "DEFAULT"}
                    {selectedRemoteHost?.Environment ? ` | ${selectedRemoteHost.Environment}` : ""}
                    {selectedRemoteHost?.IsDebugLocal ? " | Debug Local" : ""}
                  </div>
                </div>
                <div className={`table-status-chip ${getStatusToneClass(getRemoteHostStateLabel(selectedRemoteHost))}`}>
                  {getRemoteHostDisplayStateLabel(selectedRemoteHost)}
                </div>
              </div>
              <div className="batch-management-detail-grid">
                <div className="detail-item">
                  <span className="detail-label">Route</span>
                  <span className="detail-value">{getRemoteHostRouteLabel(selectedRemoteHost)}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Endpoint</span>
                  <span className="detail-value is-monospace">{selectedRemoteHost?.Endpoint ?? "-"}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Pool Profile</span>
                  <span className="detail-value is-monospace">{selectedRemoteHost?.PoolProfileId ?? "-"}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Pool Id</span>
                  <span className="detail-value is-monospace">{selectedRemoteHost?.PoolId ?? "-"}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Job Id</span>
                  <span className="detail-value is-monospace">{selectedRemoteHost?.CurrentJobId ?? "-"}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Task Id</span>
                  <span className="detail-value is-monospace">{selectedRemoteHost?.CurrentTaskId ?? "-"}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Host Instance</span>
                  <span className="detail-value is-monospace">{selectedRemoteHost?.HostInstanceId ?? "-"}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Last Started</span>
                  <span className="detail-value">{formatDateTime(selectedRemoteHost?.LastStartedDateTime)}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Last Startup Request</span>
                  <span className="detail-value">{formatDateTime(selectedRemoteHost?.LastStartupRequestedDateTime)}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Last Online</span>
                  <span className="detail-value">{formatDateTime(selectedRemoteHost?.LastOnlineDateTime)}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Last Heartbeat</span>
                  <span className="detail-value">{formatDateTime(selectedRemoteHost?.LastHeartbeatDateTime)}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Uptime</span>
                  <span className="detail-value">{getRemoteHostUptimeLabel(selectedRemoteHost) || "-"}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Startup Failures</span>
                  <span className="detail-value">{selectedRemoteHost?.StartupFailureCount ?? 0}</span>
                </div>
                <div className="detail-item detail-item-wide">
                  <span className="detail-label">Status Detail</span>
                  <span className="detail-value">{selectedRemoteHost?.LastStatusDetail ?? "No lifecycle detail recorded."}</span>
                </div>
                <div className="detail-item detail-item-wide">
                  <span className="detail-label">Last Error</span>
                  <span className="detail-value">
                    {selectedRemoteHost?.LastErrorMessage
                      ? `${formatDateTime(selectedRemoteHost?.LastErrorDateTime)} | ${selectedRemoteHost.LastErrorMessage}`
                      : "No recent host error."}
                  </span>
                </div>
              </div>
              {selectedRemoteHost?.IsDebugLocal ? (
                <div className="detail-subpanel">
                  <div className="detail-subpanel-header">
                    <h4>Debug Local Command</h4>
                    <button
                      className="row-action-button"
                      type="button"
                      onClick={() => {
                        void handleCopyDebugLocalCommand();
                      }}
                    >
                      {copiedDebugLocalCommandHostId === selectedRemoteHost?._id ? "Copied" : "Copy"}
                    </button>
                  </div>
                  <div className="detail-subpanel-meta">
                    Run this from the repo root to start the local host with the same explicit assignment Azure Batch would pass into the task.
                  </div>
                  <pre className="detail-command-block">{selectedRemoteHostDebugLocalCommand || "No debug-local command is available for this host."}</pre>
                </div>
              ) : null}
              <div className="detail-subpanel">
                <div className="detail-subpanel-header">
                  <h4>Startup Feed</h4>
                  <span>
                    {selectedRemoteHostStartupFeed?.isStreaming
                      ? "Live"
                      : selectedRemoteHostStartupFeed?.isComplete
                        ? "Complete"
                        : "Idle"}
                  </span>
                </div>
                {selectedRemoteHostStartupFeed?.eventId ? (
                  <div className="detail-subpanel-meta">
                    Event Id: <span className="is-monospace">{selectedRemoteHostStartupFeed.eventId}</span>
                  </div>
                ) : null}
                {!selectedRemoteHostStartupFeed?.messages?.length ? (
                  <div className="table-empty">No startup activity has been recorded for this host yet.</div>
                ) : (
                  <div className="detail-subpanel-scroll batch-management-startup-feed-scroll">
                    <div className="batch-management-startup-feed">
                      {selectedRemoteHostStartupFeed.messages.map((entry) => (
                        <div key={entry.id} className={`batch-management-startup-feed-entry is-${entry.uiMessageType ?? "status"}`}>
                          <div className="batch-management-startup-feed-header">
                            <span className={`table-status-chip ${getStatusToneClass(entry.uiMessageType ?? entry.stage ?? "status")}`}>
                              {entry.stage || entry.uiMessageType || "status"}
                            </span>
                            <span className="batch-management-startup-feed-time">{formatDateTime(entry.timestamp)}</span>
                          </div>
                          <div className="batch-management-startup-feed-message">{entry.message}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              <div className="detail-subpanel">
                <div className="detail-subpanel-header">
                  <h4>Related Active Tasks</h4>
                  <span>{`${selectedRemoteHostTasks.length} task(s)`}</span>
                </div>
                {selectedRemoteHostTasks.length === 0 ? (
                  <div className="table-empty">No active task rows currently match this host.</div>
                ) : (
                  <div className="detail-subpanel-scroll">
                  <div className="batch-management-table-scroll">
                    <table className="batch-management-table detail-subtable">
                      <thead>
                        <tr>
                          <th>Name</th>
                          <th>Status</th>
                          <th>Job</th>
                          <th>Task</th>
                          <th>Event</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedRemoteHostTasks.map((entry, index) => (
                          <tr key={`${entry?.HostId ?? "host"}:${entry?.EventId ?? "event"}:${entry?.JobId ?? "job"}:${entry?.TaskId ?? index}`}>
                            <td>
                              <div>{entry?.DisplayName ?? "Task"}</div>
                              {entry?.Details ? (
                                <div className="table-secondary">{entry.Details}</div>
                              ) : null}
                            </td>
                            <td>
                              <span className={`table-status-chip ${getStatusToneClass(entry?.RuntimeStatus ?? entry?.SourceStatus)}`}>
                                {entry?.RuntimeStatus ?? entry?.SourceStatus ?? "-"}
                              </span>
                            </td>
                            <td className="is-monospace">{entry?.JobId ?? "-"}</td>
                            <td className="is-monospace">{entry?.TaskId ?? entry?.NodeId ?? "-"}</td>
                            <td className="is-monospace">{entry?.EventId ?? "-"}</td>
                            <td>
                              <div className="row-actions">
                                <button
                                  className="row-action-button"
                                  type="button"
                                  disabled={!entry?.EventId}
                                  onClick={() => handleOpenBackgroundEvent(entry?.EventId)}
                                >
                                  Open Event
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  </div>
                )}
              </div>
              </div>
              </div>
            ) : null}
          </>
          )}
        </div>
      ) : null}

      {activeTab === "api-hosts" ? (
        <div className="batch-management-table-card is-remote-hosts">
          <div className="table-header">
            <h2>API Hosts</h2>
            <span>{loadingState.apiHosts ? "Loading..." : `${apiHosts.length} host(s)`}</span>
          </div>
          {sectionErrors.apiHosts ? (
            <div className="table-empty">{sectionErrors.apiHosts}</div>
          ) : loadingState.apiHosts ? (
            <div className="batch-management-section-loading"><Loading /></div>
          ) : (
          <>
            {apiHosts.length > 0 ? (
              <div className="batch-management-filter-row">
                <label className="batch-management-filter-label" htmlFor="api-host-selector">
                  API Host
                </label>
                <select
                  id="api-host-selector"
                  className="batch-management-inline-select"
                  value={selectedAPIHost ? getAPIHostRowKey(selectedAPIHost) : ""}
                  onChange={(event) => setSelectedAPIHostId(event.target.value)}
                >
                  {apiHosts.map((host) => (
                    <option key={getAPIHostRowKey(host)} value={getAPIHostRowKey(host)}>
                      {`${host?.APIName ?? "API Endpoint"} | ${host?.DeploymentKey ?? "deployment"}${host?.IsDebugLocal ? " | Debug Local" : ""}`}
                    </option>
                  ))}
                </select>
                <span className="batch-management-filter-meta">
                  Showing 1 selected host out of {apiHosts.length}
                </span>
              </div>
            ) : null}
            <div className="batch-management-table-scroll">
              <table className="batch-management-table">
                <thead>
                  <tr>
                    <th>API / Deployment</th>
                    <th>Deployment</th>
                    <th>Host</th>
                    <th>Public URL</th>
                    <th>Internal Endpoint</th>
                    <th>Batch</th>
                    <th>Heartbeat / Uptime</th>
                    <th>Lifecycle Detail</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAPIHosts.map((host) => (
                    <tr
                      key={getAPIHostRowKey(host)}
                      className={selectedAPIHost && getAPIHostRowKey(selectedAPIHost) === getAPIHostRowKey(host) ? "is-selected-row" : ""}
                      onClick={() => setSelectedAPIHostId(getAPIHostRowKey(host))}
                    >
                      <td>
                        <div>{host?.APIName ?? "API Endpoint"}</div>
                        <div className="table-secondary">
                          {host?.DeploymentKey ?? "deployment"}
                          {host?.Environment ? ` | ${host.Environment}` : ""}
                          {host?.IsDebugLocal ? " | Debug Local" : ""}
                        </div>
                      </td>
                      <td>
                        <span className={`table-status-chip ${getStatusToneClass(getDestinationStatusLabel(host?.DeploymentStatus))}`}>
                          {getDestinationStatusLabel(host?.DeploymentStatus)}
                        </span>
                      </td>
                      <td>
                        <span className={`table-status-chip ${getStatusToneClass(getAPIHostStateLabel(host))}`}>
                          {getAPIHostStateLabel(host)}
                        </span>
                      </td>
                      <td>
                        <div>
                          {host?.PublicBaseUrl ? (
                            <a href={host.PublicBaseUrl} target="_blank" rel="noreferrer">{host.PublicBaseUrl}</a>
                          ) : "-"}
                        </div>
                        <div className="table-secondary">
                          {buildAPIHostOpenApiUrl(host) ? (
                            <a href={buildAPIHostOpenApiUrl(host)} target="_blank" rel="noreferrer">OpenAPI</a>
                          ) : "No OpenAPI URL"}
                        </div>
                      </td>
                      <td>
                        <div>{host?.Endpoint ?? "-"}</div>
                        <div className="table-secondary">
                          {host?.HostInstanceId ? `Instance ${host.HostInstanceId}` : "No active instance"}
                        </div>
                      </td>
                      <td>
                        <div className="is-monospace">{host?.PoolId ?? "-"}</div>
                        <div className="table-secondary is-monospace">
                          Job: {host?.CurrentJobId ?? "-"} | Task: {host?.CurrentTaskId ?? "-"}
                        </div>
                      </td>
                      <td>
                        <div>{formatDateTime(host?.LastHeartbeatDateTime)}</div>
                        <div className="table-secondary">
                          {getAPIHostUptimeLabel(host) || "Not started"}
                        </div>
                      </td>
                      <td>
                        <div>{formatDateTime(host?.LastErrorDateTime ?? host?.LastStartupRequestedDateTime)}</div>
                        <div className="table-secondary">
                          {getAPIHostLifecycleDetail(host)}
                        </div>
                      </td>
                      <td>
                        <div className="row-actions">
                          {API_HOST_ACTIONS.map((action) => {
                            const actionKey = `${action.operationName}:${getAPIHostRowKey(host)}`;
                            const isLoading = apiHostActionKey === actionKey;
                            return (
                              <button
                                key={action.operationName}
                                className="row-action-button"
                                type="button"
                                disabled={apiHostActionKey !== "" && apiHostActionKey !== actionKey}
                                onClick={(event) => {
                                  event.stopPropagation();
                                  void handleAPIHostAction(host, action.operationName);
                                }}
                              >
                                {renderRowActionButtonContent(action.label, isLoading)}
                              </button>
                            );
                          })}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {filteredAPIHosts.length === 0 ? (
                <div className="table-empty">No API host registrations were found.</div>
              ) : null}
            </div>
            {selectedAPIHost ? (
              <div className="batch-management-detail-panel-container">
              <div className="batch-management-detail-panel">
                <div className="detail-panel-header">
                  <div>
                    <h3>{selectedAPIHost?.APIName ?? "API Endpoint"}</h3>
                    <div className="detail-panel-subtitle">
                      {selectedAPIHost?.DeploymentKey ?? "deployment"}
                      {selectedAPIHost?.Environment ? ` | ${selectedAPIHost.Environment}` : ""}
                      {selectedAPIHost?.IsDebugLocal ? " | Debug Local" : ""}
                    </div>
                  </div>
                  <div className={`table-status-chip ${getStatusToneClass(getAPIHostStateLabel(selectedAPIHost))}`}>
                    {getAPIHostStateLabel(selectedAPIHost)}
                  </div>
                </div>
                <div className="detail-grid">
                  <div className="detail-item">
                    <span className="detail-label">Deployment Status</span>
                    <span className="detail-value">{getDestinationStatusLabel(selectedAPIHost?.DeploymentStatus)}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Public URL</span>
                    <span className="detail-value">
                      {selectedAPIHost?.PublicBaseUrl ? (
                        <a href={selectedAPIHost.PublicBaseUrl} target="_blank" rel="noreferrer">{selectedAPIHost.PublicBaseUrl}</a>
                      ) : "-"}
                    </span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">OpenAPI</span>
                    <span className="detail-value">
                      {buildAPIHostOpenApiUrl(selectedAPIHost) ? (
                        <a href={buildAPIHostOpenApiUrl(selectedAPIHost)} target="_blank" rel="noreferrer">{buildAPIHostOpenApiUrl(selectedAPIHost)}</a>
                      ) : "-"}
                    </span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Internal Endpoint</span>
                    <span className="detail-value">{selectedAPIHost?.Endpoint ?? "-"}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Host Mode</span>
                    <span className="detail-value">{selectedAPIHost?.IsDebugLocal ? "Debug Local" : "Azure Batch"}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Pool</span>
                    <span className="detail-value is-monospace">{selectedAPIHost?.PoolId ?? "-"}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Listen Port</span>
                    <span className="detail-value">{selectedAPIHost?.BatchListenPort ?? "-"}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Job / Task</span>
                    <span className="detail-value is-monospace">
                      {selectedAPIHost?.CurrentJobId ?? "-"} / {selectedAPIHost?.CurrentTaskId ?? "-"}
                    </span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Last Started</span>
                    <span className="detail-value">{formatDateTime(selectedAPIHost?.LastStartedDateTime)}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Last Online</span>
                    <span className="detail-value">{formatDateTime(selectedAPIHost?.LastOnlineDateTime)}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Last Heartbeat</span>
                    <span className="detail-value">{formatDateTime(selectedAPIHost?.LastHeartbeatDateTime)}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Uptime</span>
                    <span className="detail-value">{getAPIHostUptimeLabel(selectedAPIHost) || "-"}</span>
                  </div>
                  <div className="detail-item detail-item-wide">
                    <span className="detail-label">Status Detail</span>
                    <span className="detail-value">{selectedAPIHost?.LastStatusDetail ?? "No lifecycle detail recorded."}</span>
                  </div>
                  <div className="detail-item detail-item-wide">
                    <span className="detail-label">Last Error</span>
                    <span className="detail-value">
                      {selectedAPIHost?.LastErrorMessage
                        ? `${formatDateTime(selectedAPIHost?.LastErrorDateTime)} | ${selectedAPIHost.LastErrorMessage}`
                        : "No recent host error."}
                    </span>
                  </div>
                </div>
                {selectedAPIHost?.IsDebugLocal ? (
                  <div className="detail-subpanel">
                    <div className="detail-subpanel-header">
                      <h4>Debug Local Command</h4>
                      <button
                        className="row-action-button"
                        type="button"
                        onClick={() => {
                          void handleCopyAPIHostDebugLocalCommand();
                        }}
                      >
                        {copiedAPIHostDebugLocalCommandKey === getAPIHostRowKey(selectedAPIHost) ? "Copied" : "Copy"}
                      </button>
                    </div>
                    <div className="detail-subpanel-meta">
                      Run this from the repo root to start the local API host with the same deployment assignment the hosted batch process would receive.
                    </div>
                    <pre className="detail-command-block">{selectedAPIHostDebugLocalCommand || "No debug-local command is available for this API host."}</pre>
                  </div>
                ) : null}
                <div className="detail-subpanel">
                  <div className="detail-subpanel-header">
                    <h4>Related Active Tasks</h4>
                    <span>{`${selectedAPIHostTasks.length} task(s)`}</span>
                  </div>
                  {selectedAPIHostTasks.length === 0 ? (
                    <div className="table-empty">No active task rows currently match this API host.</div>
                  ) : (
                    <div className="detail-subpanel-scroll">
                    <div className="batch-management-table-scroll">
                      <table className="batch-management-table detail-subtable">
                        <thead>
                          <tr>
                            <th>Name</th>
                            <th>Status</th>
                            <th>Job</th>
                            <th>Task</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {selectedAPIHostTasks.map((entry, index) => (
                            <tr key={`${entry?.HostId ?? "host"}:${entry?.EventId ?? "event"}:${entry?.JobId ?? "job"}:${entry?.TaskId ?? index}`}>
                              <td>
                                <div>{entry?.DisplayName ?? "Task"}</div>
                                {entry?.Details ? (
                                  <div className="table-secondary">{entry.Details}</div>
                                ) : null}
                              </td>
                              <td>
                                <span className={`table-status-chip ${getStatusToneClass(entry?.RuntimeStatus ?? entry?.SourceStatus)}`}>
                                  {entry?.RuntimeStatus ?? entry?.SourceStatus ?? "-"}
                                </span>
                              </td>
                              <td className="is-monospace">{entry?.JobId ?? "-"}</td>
                              <td className="is-monospace">{entry?.TaskId ?? entry?.NodeId ?? "-"}</td>
                              <td>
                                <div className="row-actions">
                                  <button
                                    className="row-action-button"
                                    type="button"
                                    onClick={() => void handleActiveTaskAction(entry, "stop-host")}
                                  >
                                    Drop
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    </div>
                  )}
                </div>
              </div>
              </div>
            ) : null}
          </>
          )}
        </div>
      ) : null}

      {activeTab === "background" ? (
        <div className="batch-management-table-card">
          <div className="table-header">
            <h2>Background</h2>
            <span>{loadingState.background ? "Loading..." : `${backgroundProcesses.length} item(s)`}</span>
          </div>
          {sectionErrors.background ? (
            <div className="table-empty">{sectionErrors.background}</div>
          ) : loadingState.background ? (
            <div className="batch-management-section-loading"><Loading /></div>
          ) : (
          <div className="batch-management-table-scroll">
            <table className="batch-management-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Processor</th>
                  <th>Status</th>
                  <th>Item Type</th>
                  <th>Run Date</th>
                  <th>Completed</th>
                  <th>Event Id</th>
                </tr>
              </thead>
              <tbody>
                {backgroundProcesses.map((entry) => (
                  <tr
                    key={entry?._id ?? entry?.EventId}
                    className={entry?.EventId ? "is-clickable-row" : ""}
                    onClick={() => handleOpenBackgroundEvent(entry?.EventId)}
                  >
                    <td>{entry?.DisplayName ?? "Background Event"}</td>
                    <td>
                      {PROCESSOR_TYPE_LABELS[Number(entry?.ProcessorType ?? 0)] ?? "Unknown"}
                    </td>
                    <td>
                      <span
                        className={`table-status-chip ${getStatusToneClass(
                          EVENT_STATUS_LABELS[Number(entry?.EventStatus ?? -1)] ?? ""
                        )}`}
                      >
                        {EVENT_STATUS_LABELS[Number(entry?.EventStatus ?? -1)] ?? "Unknown"}
                      </span>
                    </td>
                    <td>{entry?.ItemType ?? "-"}</td>
                    <td>{formatDateTime(entry?.RunDate ?? entry?.CreatedDate)}</td>
                    <td>{formatDateTime(entry?.CompletedDate)}</td>
                    <td className="is-monospace">
                      <span
                        className="table-ellipsis-mono is-small"
                        title={entry?.EventId ?? entry?._id ?? "-"}
                      >
                        {entry?.EventId ?? entry?._id ?? "-"}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {backgroundProcesses.length === 0 ? (
              <div className="table-empty">No background work is currently visible.</div>
            ) : null}
          </div>
          )}
        </div>
      ) : null}

      {activeTab === "scheduled" ? (
        <div className="batch-management-table-card">
          <div className="table-header">
            <h2>Scheduled Work</h2>
            <span>{loadingState.scheduled ? "Loading..." : `${scheduledWork.length} item(s)`}</span>
          </div>
          {sectionErrors.scheduled ? (
            <div className="table-empty">{sectionErrors.scheduled}</div>
          ) : loadingState.scheduled ? (
            <div className="batch-management-section-loading"><Loading /></div>
          ) : (
          <div className="batch-management-table-scroll">
            <table className="batch-management-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Processor</th>
                  <th>Event Status</th>
                  <th>Schedule Status</th>
                  <th>Interval</th>
                  <th>Start Date</th>
                  <th>End Date</th>
                  <th>Next Run</th>
                  <th>Last Run</th>
                  <th>Pool</th>
                  <th>Schedule Id</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {scheduledWork.map((entry) => (
                  <tr
                    key={entry?.EventId ?? entry?._id}
                    className={entry?.EventId ? "is-clickable-row" : ""}
                    onClick={() => handleOpenBackgroundEvent(entry?.EventId)}
                  >
                    <td>
                      <div>{entry?.DisplayName ?? "Scheduled Work"}</div>
                      {entry?.Details ? (
                        <div className="table-secondary">{entry.Details}</div>
                      ) : null}
                    </td>
                    <td>{entry?.ProcessorLabel ?? "-"}</td>
                    <td>
                      <span className={`table-status-chip ${getStatusToneClass(entry?.EventStatus)}`}>
                        {entry?.EventStatus ?? "-"}
                      </span>
                    </td>
                    <td>
                      <span className={`table-status-chip ${getStatusToneClass(entry?.ScheduleStatus)}`}>
                        {entry?.ScheduleStatus ?? "-"}
                      </span>
                    </td>
                    <td>{entry?.Interval ? `${entry.Interval} min` : "-"}</td>
                    <td>{formatDateTime(entry?.ScheduledStartDate)}</td>
                    <td>{formatScheduledEndDate(entry?.ScheduledEndDate)}</td>
                    <td>{formatDateTime(entry?.NextRunDateTime)}</td>
                    <td>{formatDateTime(entry?.LastRunDateTime)}</td>
                    <td>{entry?.PoolId ?? "-"}</td>
                    <td className="is-monospace">
                      <span
                        className="table-ellipsis-mono is-small"
                        title={entry?.JobScheduleId ?? "-"}
                      >
                        {entry?.JobScheduleId ?? "-"}
                      </span>
                    </td>
                    <td>
                      <div className="row-actions">
                        <button
                          className="row-action-button"
                          type="button"
                          disabled={scheduledActionKey !== "" && scheduledActionKey !== getScheduledActionKey(entry, "open-event")}
                          onClick={(event) => {
                            event.stopPropagation();
                            void handleScheduledAction(entry, "open-event");
                          }}
                        >
                          {renderRowActionButtonContent(
                            "Open Event",
                            scheduledActionKey === getScheduledActionKey(entry, "open-event")
                          )}
                        </button>
                        {["scheduled", "paused", "failed"].includes(String(entry?.EventStatus ?? "").toLowerCase()) ? (
                          <button
                            className="row-action-button"
                            type="button"
                            disabled={scheduledActionKey !== "" && scheduledActionKey !== getScheduledActionKey(entry, "run-now")}
                            onClick={(event) => {
                              event.stopPropagation();
                              void handleScheduledAction(entry, "run-now");
                            }}
                          >
                            {renderRowActionButtonContent(
                              "Run Now",
                              scheduledActionKey === getScheduledActionKey(entry, "run-now")
                            )}
                          </button>
                        ) : null}
                        {String(entry?.EventStatus ?? "").toLowerCase() === "scheduled" ? (
                          <button
                            className="row-action-button"
                            type="button"
                            disabled={scheduledActionKey !== "" && scheduledActionKey !== getScheduledActionKey(entry, "pause")}
                            onClick={(event) => {
                              event.stopPropagation();
                              void handleScheduledAction(entry, "pause");
                            }}
                          >
                            {renderRowActionButtonContent(
                              "Pause",
                              scheduledActionKey === getScheduledActionKey(entry, "pause")
                            )}
                          </button>
                        ) : null}
                        {String(entry?.EventStatus ?? "").toLowerCase() === "paused" ? (
                          <button
                            className="row-action-button"
                            type="button"
                            disabled={scheduledActionKey !== "" && scheduledActionKey !== getScheduledActionKey(entry, "resume")}
                            onClick={(event) => {
                              event.stopPropagation();
                              void handleScheduledAction(entry, "resume");
                            }}
                          >
                            {renderRowActionButtonContent(
                              "Resume",
                              scheduledActionKey === getScheduledActionKey(entry, "resume")
                            )}
                          </button>
                        ) : null}
                        {!["completed", "cancelled", "failed"].includes(String(entry?.EventStatus ?? "").toLowerCase()) ? (
                          <button
                            className="row-action-button"
                            type="button"
                            disabled={scheduledActionKey !== "" && scheduledActionKey !== getScheduledActionKey(entry, "cancel")}
                            onClick={(event) => {
                              event.stopPropagation();
                              void handleScheduledAction(entry, "cancel");
                            }}
                          >
                            {renderRowActionButtonContent(
                              "Cancel",
                              scheduledActionKey === getScheduledActionKey(entry, "cancel")
                            )}
                          </button>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {scheduledWork.length === 0 ? (
              <div className="table-empty">No scheduled work is currently visible.</div>
            ) : null}
          </div>
          )}
        </div>
      ) : null}

      {activeTab === "pools" ? (
        <div className="batch-management-table-card">
          <div className="table-header">
            <h2>Pools</h2>
            <span>{loadingState.pools ? "Loading..." : `${poolProfiles.length} profile(s)`}</span>
          </div>
          {sectionErrors.pools ? (
            <div className="table-empty">{sectionErrors.pools}</div>
          ) : loadingState.pools ? (
            <div className="batch-management-section-loading"><Loading /></div>
          ) : (
          <div className="batch-management-table-scroll">
            <table className="batch-management-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Status</th>
                  <th>Default</th>
                  <th>Pool Id</th>
                  <th>Type</th>
                  <th>Version</th>
                  <th>Dedicated Nodes</th>
                  <th>VM Size</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {poolProfiles.map((poolProfile) => (
                  <tr key={poolProfile?._id ?? poolProfile?.PoolId}>
                    <td>{poolProfile?.Name ?? "Pool Profile"}</td>
                    <td>
                      <span
                        className={`table-status-chip ${poolProfile?.IsActive === false ? "is-warn" : "is-ok"}`}
                      >
                        {poolProfile?.IsActive === false ? "Inactive" : "Active"}
                      </span>
                    </td>
                    <td>
                      {poolProfile?.IsDefault ? (
                        <span className="table-status-chip is-ok">Default</span>
                      ) : (
                        "-"
                      )}
                    </td>
                    <td className="is-monospace">{poolProfile?.PoolId ?? "-"}</td>
                    <td>{poolProfile?.PoolType ?? "-"}</td>
                    <td>{poolProfile?.Version ?? "-"}</td>
                    <td>{poolProfile?.TargetDedicatedComputeNodes ?? 0}</td>
                    <td>{poolProfile?.VirtualMachineSize ?? "-"}</td>
                    <td>
                      <div className="row-actions">
                        {POOL_ACTIONS.map((action) => (
                          <button
                            key={`${action.label}:${poolProfile?._id ?? poolProfile?.PoolId}`}
                            className="row-action-button"
                            type="button"
                            disabled={
                              action.isManualTest
                                ? isRunningManualTest
                                : action.operationName
                                  ? poolActionKey !== "" && poolActionKey !== `${action.operationName}:${poolProfile?._id}`
                                  : false
                            }
                            onClick={() => {
                              if (action.isManualTest) {
                                void handlePoolManualTest(poolProfile);
                                return;
                              }

                              if (action.isOpen) {
                                handleOpenPoolProfile(poolProfile?._id);
                                return;
                              }

                              void handlePoolAction(poolProfile, action.operationName);
                            }}
                          >
                            {action.isManualTest && activeManualTestActionKey === `pool-check:${poolProfile?._id}` ? (
                              renderRowActionButtonContent(action.label, true)
                            ) : action.operationName && poolActionKey === `${action.operationName}:${poolProfile?._id}`
                              ? renderRowActionButtonContent(action.label, true)
                              : renderRowActionButtonContent(action.label, false)}
                          </button>
                        ))}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {poolProfiles.length === 0 ? (
              <div className="table-empty">No active pool profiles were found.</div>
            ) : null}
          </div>
          )}
        </div>
      ) : null}

      {activeTab === "tests" ? (
        <div className="batch-management-table-card">
          <div className="table-header">
            <h2>Manual Test History</h2>
            <div className="batch-management-inline-actions">
              <span>{`${manualTestHistory.length} result(s)`}</span>
              <button
                className="row-action-button"
                type="button"
                disabled={isRunningManualTest}
                onClick={() => openManualTestDialog(
                  selectedManualTestHistoryEntry?.key ?? "azure-batch",
                  "tests:open-runner"
                )}
              >
                {activeManualTestActionKey === "tests:open-runner" ? (
                  <span className="row-action-content is-loading">
                    <span className="row-action-spinner" />
                    <span>Open Runner</span>
                  </span>
                ) : "Open Runner"}
              </button>
            </div>
          </div>

          <div className="batch-management-tests-layout">
            <div className="batch-management-tests-list">
              {manualTestHistory.length === 0 ? (
                <div className="table-empty">No manual tests have been recorded yet.</div>
              ) : (
                manualTestHistory.map((entry) => (
                  <button
                    key={entry.id}
                    type="button"
                    className={`batch-management-test-history-row ${
                      selectedManualTestHistoryEntry?.id === entry.id ? "is-active" : ""
                    }`}
                    onClick={() => setSelectedManualTestHistoryId(entry.id)}
                  >
                    <div className="test-history-title-row">
                      <span className="test-option-title">{entry.label}</span>
                      <span className={`table-status-chip ${getStatusToneClass(entry.payload?.Status ?? entry.result?.DisplayMessage)}`}>
                        {entry.payload?.Status ?? (entry.result?.Success ? "Passed" : "Failed")}
                      </span>
                    </div>
                    <div className="test-option-meta">
                      {entry.scopeLabel || "System"}
                      {entry.payload?.CheckedDateTime ? ` | ${formatDateTime(entry.payload.CheckedDateTime)}` : ""}
                    </div>
                  </button>
                ))
              )}
            </div>

            <div className="batch-management-test-result">
              {selectedManualTestHistoryEntry ? (
                <>
                  <div className="test-result-title">{selectedManualTestHistoryEntry.label}</div>
                  <div className="test-result-meta">
                    {selectedManualTestHistoryEntry.scopeLabel || "System"}
                  </div>
                  <div
                    className={`batch-management-message ${
                      selectedManualTestHistoryEntry.result?.Success ? "is-ok" : "is-error"
                    }`}
                  >
                    <div className="message-title">
                      {selectedManualTestHistoryEntry.result?.DisplayMessage ?? "Manual test result"}
                    </div>
                    {selectedManualTestHistoryEntry.payload ? (
                      <div className="message-meta">
                        {selectedManualTestHistoryEntry.payload?.TestName ? (
                          <span>{selectedManualTestHistoryEntry.payload.TestName}</span>
                        ) : null}
                        {getPayloadStatusLabel(selectedManualTestHistoryEntry.payload) ? (
                          <span>Status: {getPayloadStatusLabel(selectedManualTestHistoryEntry.payload)}</span>
                        ) : null}
                        {selectedManualTestHistoryEntry.payload?.CheckedDateTime ? (
                          <span>
                            Checked: {formatDateTime(selectedManualTestHistoryEntry.payload.CheckedDateTime)}
                          </span>
                        ) : null}
                      </div>
                    ) : null}
                    {selectedManualTestHistoryDetailLines.map((entry) => (
                      <div key={entry} className="message-detail">
                        {entry}
                      </div>
                    ))}
                    {renderCapabilityResults(selectedManualTestHistoryEntry.payload?.CapabilityResults)}
                  </div>
                </>
              ) : (
                <div className="batch-management-test-empty">
                  Run a manual test to build history here.
                </div>
              )}
            </div>
          </div>
        </div>
      ) : null}

      {activeTab === "diagnostics" ? (
        <>
          <div className="batch-management-card-grid">
            <article className="batch-management-card">
              <div className="card-label">Auto Refresh</div>
              <div className="card-value">{diagnosticsOverview.autoRefreshEnabled ? "On" : "Off"}</div>
              <div className="card-meta">
                {AUTO_REFRESH_INTERVAL_SECONDS}s cadence
                {diagnosticsOverview.autoRefreshBlocked ? " | currently blocked" : ""}
              </div>
            </article>
            <article className="batch-management-card">
              <div className="card-label">Last Refresh</div>
              <div className="card-value">
                {lastRefreshDateTime ? formatDateTime(lastRefreshDateTime) : "-"}
              </div>
              <div className="card-meta">Latest full screen refresh</div>
            </article>
            <article className="batch-management-card">
              <div className="card-label">Section Errors</div>
              <div className="card-value">{diagnosticsOverview.sectionErrorCount}</div>
              <div className="card-meta">Current section-level load failures</div>
            </article>
            <article className="batch-management-card">
              <div className="card-label">Stale Hosts</div>
              <div className="card-value">{diagnosticsOverview.staleHostCount}</div>
              <div className="card-meta">Heartbeat older than {REMOTE_HOST_STALE_SECONDS}s</div>
            </article>
            <article className="batch-management-card">
              <div className="card-label">API Host Errors</div>
              <div className="card-value">{diagnosticsOverview.apiHostErrorCount}</div>
              <div className="card-meta">Visible API hosts currently reporting an error state</div>
            </article>
            <article className="batch-management-card">
              <div className="card-label">Draining Hosts</div>
              <div className="card-value">{diagnosticsOverview.drainingHostCount}</div>
              <div className="card-meta">Hosts blocking new remote work</div>
            </article>
            <article className="batch-management-card">
              <div className="card-label">Startup Failures</div>
              <div className="card-value">{diagnosticsOverview.startupFailureCount}</div>
              <div className="card-meta">Recorded startup failures across visible hosts</div>
            </article>
          </div>

          <div className="batch-management-table-card">
            <div className="table-header">
              <h2>Section Status</h2>
              <span>{`${sectionDiagnosticRows.length} section(s)`}</span>
            </div>
            <div className="batch-management-table-scroll">
              <table className="batch-management-table">
                <thead>
                  <tr>
                    <th>Section</th>
                    <th>State</th>
                    <th>Items</th>
                    <th>Error</th>
                  </tr>
                </thead>
                <tbody>
                  {sectionDiagnosticRows.map((entry) => (
                    <tr key={entry.key}>
                      <td>{entry.label}</td>
                      <td>
                        <span className={`table-status-chip ${entry.error ? "is-error" : entry.isLoading ? "is-warn" : "is-ok"}`}>
                          {entry.error ? "Error" : entry.isLoading ? "Loading" : "Ready"}
                        </span>
                      </td>
                      <td>{entry.itemCount}</td>
                      <td>{entry.error || "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="batch-management-panel-grid">
            <section className="batch-management-panel">
              <div className="panel-header">
                <h2>Remote Host Startup Diagnostics</h2>
                <span>{`${startupDiagnosticHosts.length} host(s)`}</span>
              </div>
              <div className="mini-list">
                {startupDiagnosticHosts.map((host) => (
                  <div key={host?._id} className="mini-list-row">
                    <div>
                      <div className="mini-list-title">{host?.Name ?? "Remote Batch Host"}</div>
                      <div className="mini-list-detail">
                        {host?.ClientShortName ?? "DEFAULT"}
                        {host?.LastStartupRequestedDateTime ? ` | Requested ${formatDateTime(host.LastStartupRequestedDateTime)}` : ""}
                        {host?.LastOnlineDateTime ? ` | Online ${formatDateTime(host.LastOnlineDateTime)}` : ""}
                      </div>
                      <div className="mini-list-detail">
                        {host?.LastStatusDetail ?? host?.LastErrorMessage ?? "No startup detail recorded."}
                      </div>
                    </div>
                    <div className={`mini-list-status ${getStatusToneClass(getRemoteHostStateLabel(host))}`}>
                      {getRemoteHostStateLabel(host)}
                      {host?.StartupFailureCount ? ` (${host.StartupFailureCount})` : ""}
                    </div>
                  </div>
                ))}
                {startupDiagnosticHosts.length === 0 ? (
                  <div className="mini-list-empty">No startup diagnostics are currently recorded.</div>
                ) : null}
              </div>
            </section>

            <section className="batch-management-panel">
              <div className="panel-header">
                <h2>Stale Remote Hosts</h2>
                <span>{`${staleRemoteHosts.length} host(s)`}</span>
              </div>
              <div className="mini-list">
                {staleRemoteHosts.map((host) => (
                  <div key={host?._id} className="mini-list-row">
                    <div>
                      <div className="mini-list-title">{host?.Name ?? "Remote Batch Host"}</div>
                      <div className="mini-list-detail">
                        {host?.ClientShortName ?? "DEFAULT"} | {formatDateTime(host?.LastHeartbeatDateTime)}
                      </div>
                    </div>
                    <div className="mini-list-status is-warn">Stale</div>
                  </div>
                ))}
                {staleRemoteHosts.length === 0 ? (
                  <div className="mini-list-empty">No stale remote hosts are currently visible.</div>
                ) : null}
              </div>
            </section>

            <section className="batch-management-panel">
              <div className="panel-header">
                <h2>API Host Diagnostics</h2>
                <span>{`${apiDiagnosticHosts.length} host(s)`}</span>
              </div>
              <div className="mini-list">
                {apiDiagnosticHosts.map((host) => (
                  <div key={getAPIHostRowKey(host)} className="mini-list-row">
                    <div>
                      <div className="mini-list-title">
                        {host?.APIName ?? "API Endpoint"} | {host?.DeploymentKey ?? "deployment"}
                      </div>
                      <div className="mini-list-detail">
                        {getDestinationStatusLabel(host?.DeploymentStatus)}
                        {host?.LastStartupRequestedDateTime ? ` | Requested ${formatDateTime(host.LastStartupRequestedDateTime)}` : ""}
                        {host?.LastOnlineDateTime ? ` | Online ${formatDateTime(host.LastOnlineDateTime)}` : ""}
                      </div>
                      <div className="mini-list-detail">
                        {host?.LastStatusDetail ?? host?.LastErrorMessage ?? "No lifecycle detail recorded."}
                      </div>
                    </div>
                    <div className={`mini-list-status ${getStatusToneClass(getAPIHostStateLabel(host))}`}>
                      {getAPIHostStateLabel(host)}
                    </div>
                  </div>
                ))}
                {apiDiagnosticHosts.length === 0 ? (
                  <div className="mini-list-empty">No API host diagnostics are currently recorded.</div>
                ) : null}
              </div>
            </section>

            <section className="batch-management-panel">
              <div className="panel-header">
                <h2>Stale API Hosts</h2>
                <span>{`${staleAPIHosts.length} host(s)`}</span>
              </div>
              <div className="mini-list">
                {staleAPIHosts.map((host) => (
                  <div key={getAPIHostRowKey(host)} className="mini-list-row">
                    <div>
                      <div className="mini-list-title">
                        {host?.APIName ?? "API Endpoint"} | {host?.DeploymentKey ?? "deployment"}
                      </div>
                      <div className="mini-list-detail">
                        {host?.PublicBaseUrl ?? host?.Endpoint ?? "No endpoint"} | {formatDateTime(host?.LastHeartbeatDateTime)}
                      </div>
                    </div>
                    <div className="mini-list-status is-warn">Stale</div>
                  </div>
                ))}
                {staleAPIHosts.length === 0 ? (
                  <div className="mini-list-empty">No stale API hosts are currently visible.</div>
                ) : null}
              </div>
            </section>

            <section className="batch-management-panel">
              <div className="panel-header">
                <h2>Latest Manual Test</h2>
                <span>{selectedManualTestHistoryEntry ? "Saved for current client" : "No test yet"}</span>
              </div>
              {selectedManualTestHistoryEntry ? (
                <div
                  className={`batch-management-message ${
                    selectedManualTestHistoryEntry.result?.Success ? "is-ok" : "is-error"
                  }`}
                >
                  <div className="message-title">
                    {selectedManualTestHistoryEntry.result?.DisplayMessage ?? selectedManualTestHistoryEntry.label}
                  </div>
                  <div className="message-meta">
                    <span>{selectedManualTestHistoryEntry.scopeLabel || "System"}</span>
                    <span>{formatDateTime(selectedManualTestHistoryEntry.checkedDateTime)}</span>
                  </div>
                  {selectedManualTestHistoryDetailLines.map((entry) => (
                    <div key={entry} className="message-detail">{entry}</div>
                  ))}
                  {renderCapabilityResults(selectedManualTestHistoryEntry.payload?.CapabilityResults)}
                </div>
              ) : (
                <div className="table-empty">Run a manual test to populate diagnostics.</div>
              )}
            </section>
          </div>

          <div className="batch-management-panel-grid">
            <section className="batch-management-panel">
              <div className="panel-header">
                <h2>Summary Snapshot</h2>
                <span>Current aggregate payload</span>
              </div>
              <pre className="diagnostic-pre">{toDiagnosticJson(summary)}</pre>
            </section>

            <section className="batch-management-panel">
              <div className="panel-header">
                <h2>Selected Remote Host Snapshot</h2>
                <span>{selectedRemoteHost ? selectedRemoteHost.Name ?? "Host" : "No host selected"}</span>
              </div>
              <pre className="diagnostic-pre">{toDiagnosticJson(selectedRemoteHost)}</pre>
            </section>

            <section className="batch-management-panel">
              <div className="panel-header">
                <h2>Selected API Host Snapshot</h2>
                <span>{selectedAPIHost ? `${selectedAPIHost.APIName ?? "API Endpoint"} | ${selectedAPIHost.DeploymentKey ?? "deployment"}` : "No host selected"}</span>
              </div>
              <pre className="diagnostic-pre">{toDiagnosticJson(selectedAPIHost)}</pre>
            </section>
          </div>

          <div className="batch-management-panel-grid">
            <section className="batch-management-panel">
              <div className="panel-header">
                <h2>Last Action Result</h2>
                <span>{lastActionResult ? "Latest operator action" : "No action yet"}</span>
              </div>
              <pre className="diagnostic-pre">{toDiagnosticJson(lastActionResult)}</pre>
            </section>

            <section className="batch-management-panel">
              <div className="panel-header">
                <h2>Screen Diagnostics</h2>
                <span>UI runtime snapshot</span>
              </div>
              <pre className="diagnostic-pre">{toDiagnosticJson(diagnosticsOverview)}</pre>
            </section>
          </div>
        </>
      ) : null}
      </div>
      </div>
    </div>
  );
};

export default BatchManagement;
